import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import "./style.css";
import InputMask from "../../components/InputMask/index.js";
import { NumericFormat } from "react-number-format";
import { useForm } from "react-hook-form";
import { TbDoorExit } from "react-icons/tb";
import { jwtDecode } from "jwt-decode";
import { API_ENDPOINTS, API_BASE_URL } from "../../../js/api.js";
import FormReview from "../../components/Compos Validation/index.js";
import MissingFieldsAlert from "../../components/Fill-inAlert/index.js";
import ToastContainer from "../../components/Toast/ToastContainer.js";
import useToast from "../../hooks/useToast.js";
import useConfirm from "../../hooks/useConfirm.js";
import DocumentUploadForm from "../../components/DocumentUploadForm/index.js";
import {
  Check,
  CheckCircle2,
  AlertCircle,
  Circle,
  Upload,
  Paperclip,
  Home,
  GraduationCap,
  IdCard,
  FileText,
  Trash2,
} from "lucide-react";

const LINK_DOCUSIGN_TCA_CIEEMG_FALLBACK =
  "https://na2.docusign.net/Member/PowerFormSigning.aspx?PowerFormId=332d1eb3-e52c-497a-bcf9-da18ae0d0234&env=na2&acct=272fb0e3-8eca-4797-ac82-b4ba543c625f&v=2";


const CAMPOS_APRENDIZ_BLOQUEADOS_EMPRESA = new Set([
  "nome_mae",
  "cpf_mae",
  "telefone_mae",
  "email_mae",
  "data_nascimento_mae",
  "naturalidade_mae",
  "estado_civil_mae",
  "logradouro",
  "numero_endereco",
  "complemento",
  "bairro",
  "cep",
  "cidade",
  "estado_endereco",
  "pais",
  "telefone_02",
  "grau_instrucao",
  "curso_escolaridade",
  "instituicao_ensino",
  "conclusao",
  "pis",
  "rg",
  "data_emissao_rg",
  "orgao_emissor_rg",
  "estado_rg",
  "titulo_eleitor",
  "zona",
  "secao",
  "data_emissao_titulo",
  "banco_pagamento",
  "agencia",
  "div1",
  "conta",
  "div2",
  "trabalhou_ambiente_insalubre",
  "risco_exposto",
  "endereco_residencial_vt",
  "servicos_meios_transporte",
  "qtd_dia_pratica",
  "linha_pratica",
  "tarifa_pratica",
  "qtd_dia_teorica",
  "linha_teorica",
  "tarifa_teorica",
  "deseja_vale_transporte",
  "possui_dependentes",
  "nome_declarante_ir",
  "cpf_declarante_ir",
  "estado_civil_ir",
  "doc_comprovante_endereco",
  "doc_declaracao_escolar",
  "doc_identificacao_documento_indetificacao",
  "doc_pis",
  "doc_tipo_sanguineo",
  "doc_certificado_reservista",
  "doc_certidao_casamento",
  "doc_titulo_eleitor",
  "doc_curriculum",
  "doc_curso_qualificacao",
  "doc_foto_3x4",
]);

for (let i = 1; i <= 10; i += 1) {
  [
    "nome",
    "cpf",
    "data_nascimento",
    "idade",
    "sexo",
    "grau_parentesco",
    "naturalidade",
  ].forEach((sufixo) => {
    CAMPOS_APRENDIZ_BLOQUEADOS_EMPRESA.add(`dependente_${i}_${sufixo}`);
  });
}

const STATUS_CORES = {
  PENDENTE: "#3b82f6",
  APROVADO: "#1e3a8a",
  RECUSADO: "#dc2626",
};

const formatDateBR = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const formatarCnpjCpf = (valor) => {
  if (!valor) return "";

  const nums = valor.replace(/\D/g, "").slice(0, 14);
  if (nums.length === 0) return "";
  if (nums.length <= 11) {
    return nums
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  } else {
    return nums
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  }
};

const isTelefonePlaceholder = (valor) => {
  if (!valor) return false;
  const nums = String(valor).replace(/\D/g, "");
  if (nums.length < 8) return false;
  return /^(\d)\1+$/.test(nums);
};

const sanitizeTelefone = (valor) => {
  if (!valor) return "";
  if (isTelefonePlaceholder(valor)) return "";
  return String(valor);
};

const getAutorizacao = async (empresaId) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/tca/empresa/${empresaId}/autorizacao`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  } catch (error) {
    console.error("Erro autorização:", error);
    return null;
  }
};

const parseSalario = (val) => {
  if (val === null || val === undefined) return 0;
  if (typeof val === "number") return val;
  const cleaned = String(val)
    .replace("R$", "")
    .replace(/\s/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

const TcaCieemgEmpregador = () => {
  const navigate = useNavigate();
  const submittingRef = useRef(false);
  const matrizEnderecoTeoricoRef = useRef("");
  const matrizEnderecoRef = useRef({});
  const tcaForm = useForm({ mode: "onSubmit" });
  const { handleSubmit: rhfHandleSubmit, setValue: setTcaFormValue } = tcaForm;
  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, []);
  const [autorizado, setAutorizado] = useState(false);
  const [permissao, setPermissao] = useState({});
  const [autorizacaoTca, setAutorizacaoTca] = useState(null);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [tcaId, setTcaId] = useState(null);
  const [tcaStatus, setTcaStatus] = useState(null);
  const [datasVigenciaFuturas, setDatasVigenciaFuturas] = useState([]);
  const [cursosFiltrados, setCursosFiltrados] = useState([]);
  const [tcaHistorico, setTcaHistorico] = useState([]);
  const [linkDocuSign, setLinkDocuSign] = useState("");
  const [showReview, setShowReview] = useState(false);
  const [missingFieldsAlert, setMissingFieldsAlert] = useState([]);
  const [salarioMinimoVigente, setSalarioMinimoVigente] = useState(1621.0);
  const [unidadesCieemg, setUnidadesCieemg] = useState([]);
  const [horariosTeoricos, setHorariosTeoricos] = useState([]);
  const [dependentesList, setDependentesList] = useState([1]);
  const [mostrarDeclaracaoDependentes, setMostrarDeclaracaoDependentes] =
    useState(false);
  const [cienteDeclaracaoDependentes, setCienteDeclaracaoDependentes] =
    useState(false);
  const {
    toasts,
    error: toastError,
    success: toastSuccess,
    warning: toastWarning,
    removeToast,
  } = useToast();
  const {
    confirm,
    prompt: promptUser,
    confirmDialog: confirmDialogEmpregador,
  } = useConfirm();
  const [formData, setFormData] = useState({
    nome_filial: "",
    razao_social: "",
    cnpj_ou_cpf: "",
    telefone_empresa: "",
    email_empresa: "",
    nome_representante_legal: "",
    cargo_representante_legal: "",
    telefone_representante_legal: "",
    email_representante_legal: "",
    data_inicio_contrato: "",
    salario: "",
    tutor_aprendiz_empresa: "",
    cargo_tutor: "",
    nome_tutor: "",
    email_tutor: "",
    endereco_aprendizagem_pratica: "",
    endereco_aprendizagem_teorica: "",
    carga_horaria_diaria: "",
    curso: "",
    horario_pratica_inicio: "",
    horario_pratica_fim: "",
    horario_teorica_inicio: "",
    horario_teorica_fim: "",
    nome_completo: "",
    nome_social: "",
    estado_natal: "",
    naturalidade: "",
    nacionalidade: "",
    data_nascimento: "",
    sexo: "",
    nome_aprendiz_menor_idade: "",
    telefone_aprendiz_menor_idade: "",
    email_aprendiz_menor_idade: "",
    cpf_aprendiz_menor_idade: "",
    tipo_sanguineo: "",
    cor: "",
    fumante: false,
    estado_civil: "",
    nome_mae: "",
    email_mae: "",
    telefone_mae: "",
    cpf_mae: "",
    data_nascimento_mae: "",
    naturalidade_mae: "",
    estado_civil_mae: "",
    logradouro: "",
    local_realizacao_atividades: "",
    numero_endereco: "",
    complemento: "",
    bairro: "",
    cep: "",
    regiao: "",
    cidade: "",
    estado_endereco: "",
    pais: "",
    telefone_01: "",
    telefone_02: "",
    email_pessoal: "",
    grau_instrucao: "",
    curso_escolaridade: "",
    instituicao_ensino: "",
    conclusao: "",
    cpf: "",
    pis: "",
    rg: "",
    data_emissao_rg: "",
    orgao_emissor_rg: "",
    estado_rg: "",
    titulo_eleitor: "",
    zona: "",
    secao: "",
    data_emissao_titulo: "",
    estado_titulo: "",
    numero_certificado_reservista: "",
    data_emissao_certificado: "",
    orgao_expedicao: "",
    situacao_militar: "",
    banco_pagamento: "Bradesco 237",
    agencia: "",
    div1: "",
    conta: "",
    div2: "",
    trabalhou_ambiente_insalubre: false,
    risco_exposto: "",
    endereco_residencial_vt: "",
    servicos_meios_transporte: "",
    qtd_dia_pratica: "",
    linha_pratica: "",
    tarifa_pratica: "",
    qtd_dia_teorica: "",
    linha_teorica: "",
    tarifa_teorica: "",
    deseja_vale_transporte: false,
    nome_declarante_ir: "",
    cpf_declarante_ir: "",
    estado_civil_ir: "",
    dependente_1_nome: "",
    dependente_1_cpf: "",
    dependente_1_data_nascimento: "",
    dependente_1_idade: "",
    dependente_1_sexo: "",
    dependente_1_grau_parentesco: "",
    dependente_1_naturalidade: "",
    dependente_2_nome: "",
    dependente_2_cpf: "",
    dependente_2_data_nascimento: "",
    dependente_2_idade: "",
    dependente_2_sexo: "",
    dependente_2_grau_parentesco: "",
    dependente_2_naturalidade: "",
    dependente_3_nome: "",
    dependente_3_cpf: "",
    dependente_3_data_nascimento: "",
    dependente_3_idade: "",
    dependente_3_sexo: "",
    dependente_3_grau_parentesco: "",
    dependente_3_naturalidade: "",
    doc_comprovante_endereco: false,
    doc_declaracao_escolar: false,
    doc_identificacao_documento_indetificacao: false,
    doc_pis: false,
    doc_tipo_sanguineo: false,
    doc_certificado_reservista: false,
    doc_certidao_casamento: false,
    doc_titulo_eleitor: false,
    doc_curriculum: false,
    doc_curso_qualificacao: false,
    doc_foto_3x4: false,
    observacoes_ajuste_contrato: "",
    inserir_observacoes: false,
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [filiais, setFiliais] = useState([]);
  const [mostrarFiliais, setMostrarFiliais] = useState(false);
  const [localAtividade, setLocalAtividade] = useState("");
  const [filialLoading, setFilialLoading] = useState(false);
  const [anexosFiles, setAnexosFiles] = useState({});
  const [anexosProgress, setAnexosProgress] = useState({});
  const [anexosStatus, setAnexosStatus] = useState({});
  const inputRef = useRef(null);
  const [idadeCalculada, setIdadeCalculada] = useState(null);
  const [estudanteNaoEncontrado, setEstudanteNaoEncontrado] = useState(false);
  const [cienteCadastro, setCienteCadastro] = useState(false);

  const buscarDadosDoAprendiz = async (cpfDigitado) => {
    if (!cpfDigitado) return;
    const cpfLimpo = String(cpfDigitado).replace(/\D/g, "");

    if (cpfLimpo.length !== 11) return;

    try {
      setEstudanteNaoEncontrado(false);
      setCienteCadastro(false);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/estudantes/cpf/${cpfLimpo}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const dadosDoEstudante = response.data;
      console.log(
        "=== DADOS DO ESTUDANTE RETORNADOS PELA API ===",
        dadosDoEstudante,
      );

      if (dadosDoEstudante) {

        const blocoDadosAprendiz = {
          nome_completo: dadosDoEstudante.nome_completo || "",
          nome_social: dadosDoEstudante.nome_social || "",
          estado_natal: dadosDoEstudante.uf_naturalidade || "",
          naturalidade: dadosDoEstudante.naturalidade || "",
          nacionalidade: dadosDoEstudante.nacionalidade || "",
          data_nascimento:
            dadosDoEstudante.dt_nascimento ||
            dadosDoEstudante.data_nascimento ||
            "",
          sexo: dadosDoEstudante.sexo || "",
          estado_civil: dadosDoEstudante.estadocivil || "",
          telefone_01: dadosDoEstudante.telefone || "",
          email_pessoal: dadosDoEstudante.email || "",
        };

        const blocoResponsabilidadeAprendiz = {
          nome_mae: dadosDoEstudante.nomemae || "",
          cpf_mae: dadosDoEstudante.cpf_mae || "",
          logradouro: dadosDoEstudante.logradouro || "",
          numero_endereco: dadosDoEstudante.numero || "",
          complemento: dadosDoEstudante.complemento || "",
          bairro: dadosDoEstudante.bairro || "",
          cep: dadosDoEstudante.cep || "",
          cidade: dadosDoEstudante.cidade || "",
          estado_endereco: dadosDoEstudante.uf || "",
          telefone_02: dadosDoEstudante.telefone2 || "",
          curso_escolaridade:
            dadosDoEstudante.curso_id ||
            dadosDoEstudante.curso_nome ||
            "",
          instituicao_ensino:
            dadosDoEstudante.escola_id ||
            dadosDoEstudante.escola_nome ||
            "",
          conclusao:
            dadosDoEstudante.previsaoformatura ||
            dadosDoEstudante.previsao_formatura ||
            "",
          rg: dadosDoEstudante.rg || "",
          orgao_emissor_rg: dadosDoEstudante.orgaoexpedidor || "",

        };

        setFormData((prev) => ({
          ...prev,
          ...blocoDadosAprendiz,
          ...blocoResponsabilidadeAprendiz,
        }));
      }
    } catch (error) {
      console.log("Estudante não encontrado ou erro na busca", error);
      setEstudanteNaoEncontrado(true);
      setCienteCadastro(false);
      setFormData((prev) => ({
        ...prev,
        nome_completo: "",
        telefone_01: "",
        email_pessoal: "",
        data_nascimento: "",
      }));
    }
  };



  useEffect(() => {
    const acesso = Number(user?.acesso_autorizacao);
    if (acesso !== 0) return;

    const timer = setTimeout(() => {
      const cpfValue = formData.cpf || "";
      const cpfLimpo = String(cpfValue).replace(/\D/g, "");

      if (cpfLimpo.length < 11) {
        setEstudanteNaoEncontrado(false);
        if (cpfLimpo.length === 0) {
          setFormData((prev) => {
            if (
              prev.nome_completo ||
              prev.telefone_01 ||
              prev.email_pessoal ||
              prev.data_nascimento
            ) {
              return {
                ...prev,
                nome_completo: "",
                telefone_01: "",
                email_pessoal: "",
                data_nascimento: "",
              };
            }
            return prev;
          });
        }
        return;
      }

      buscarDadosDoAprendiz(cpfValue);
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.cpf, user?.acesso_autorizacao]);

  const handleAnexoChange = (key) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ["application/pdf", "image/png", "image/jpeg"];
    if (!allowed.includes(file.type)) {
      toastWarning("Formato inválido. Use PDF, PNG ou JPG.");
      e.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastWarning("Arquivo maior que 10MB.");
      e.target.value = "";
      return;
    }

    setAnexosFiles((prev) => ({ ...prev, [key]: file }));
    setAnexosStatus((prev) => ({ ...prev, [key]: "uploading" }));
    setAnexosProgress((prev) => ({ ...prev, [key]: 0 }));
    let prog = 0;

    const interval = setInterval(() => {
      prog += 20;
      if (prog >= 100) {
        prog = 100;
        clearInterval(interval);
        setAnexosStatus((prev) => ({ ...prev, [key]: "done" }));
      }
      setAnexosProgress((prev) => ({ ...prev, [key]: prog }));
    }, 80);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !user) {
      window.location.href = "/";
      return;
    }
    const nivel = Number(user.acesso_autorizacao ?? 0);
    const niveisPermitidos = [0, 1, 2, 3, 4];
    if (!niveisPermitidos.includes(nivel)) {
      window.location.href = "/sem-permissao";
      return;
    }
    setPermissao({
      podeEditarEmpresa: [0, 2, 4].includes(nivel),
      podeEditarEstudante: [1, 2, 4].includes(nivel),
      podeEditarTudo: [2, 4].includes(nivel),
      podeAprovar: [3, 4].includes(nivel),
      somenteLeituraEstudante: [0].includes(nivel),
    });
    setAutorizado(true);
  }, [user]);


  const isEmpresaReadOnly = [0].includes(Number(user?.acesso_autorizacao));
  const isCampoBloqueadoParaEmpresa = (name) => {
    if (!name) return false;
    if (Number(user?.acesso_autorizacao) !== 0) return false;
    return CAMPOS_APRENDIZ_BLOQUEADOS_EMPRESA.has(name);
  };

  const isEstudante = Number(user?.acesso_autorizacao) === 1;

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const token = localStorage.getItem("token");
        const user = JSON.parse(localStorage.getItem("user"));
        const empresaId = user?.empresa_id;

        // Se houver ?id= na URL, é edição de TCA existente.
        // NÃO preenche formData com dados da empresa — preserva o que veio do TCA.
        const params = new URLSearchParams(window.location.search);
        const editingId = params.get("id");
        if (editingId) {
          if (mounted) setLoading(false);
          return;
        }

        if (empresaId) {
          const respEmp = await axios.get(`${API_BASE_URL}/tca/dados-empresa`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const enderecoTeorico =
            respEmp.data.endereco_aprendizagem_teorica ||
            respEmp.data.unidadeTeorica?.endereco ||
            "";
          matrizEnderecoTeoricoRef.current = enderecoTeorico;
          console.log(
            "[TcaEmpregador] load(): enderecoTeorico (fallback) = ",
            enderecoTeorico,
          );

          matrizEnderecoRef.current = {
            logradouro: respEmp.data.logradouro || "",
            numero_endereco: respEmp.data.numero_endereco || "",
            complemento: respEmp.data.complemento || "",
            bairro: respEmp.data.bairro || "",
            cidade: respEmp.data.cidade || "",
            estado_endereco: respEmp.data.estado_endereco || "",
            cep: respEmp.data.cep || "",
          };

          setFormData((prev) => ({
            ...prev,
            empresa_id: empresaId,
            // Só preenche se ainda estiver vazio no formData — evita
            // sobrescrever dados de um TCA existente carregado por outro perfil.
            razao_social: prev.razao_social || respEmp.data.razao_social || "",
            cnpj_ou_cpf: prev.cnpj_ou_cpf || respEmp.data.cnpj_ou_cpf || "",
            telefone_empresa:
              prev.telefone_empresa ||
              respEmp.data.telefone_empresa_primario ||
              "",
            email_empresa:
              prev.email_empresa ||
              respEmp.data.email ||
              respEmp.data.email_empresa ||
              "",
            logradouro: prev.logradouro || respEmp.data.logradouro || "",
            numero_endereco:
              prev.numero_endereco || respEmp.data.numero_endereco || "",
            complemento: prev.complemento || respEmp.data.complemento || "",
            bairro: prev.bairro || respEmp.data.bairro || "",
            cidade: prev.cidade || respEmp.data.cidade || "",
            regiao: prev.regiao || respEmp.data.regiao || "",
            estado_endereco:
              prev.estado_endereco || respEmp.data.estado_endereco || "",
            cep: prev.cep || respEmp.data.cep || "",
            endereco_aprendizagem_teorica:
              prev.endereco_aprendizagem_teorica || enderecoTeorico,
          }));

          const respAut = await axios.get(
            `${API_BASE_URL}/tca/empresa/${empresaId}/autorizacao`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          setAutorizacaoTca(respAut.data);

          const respFiliais = await axios.get(`${API_BASE_URL}/tca/filiais`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (respFiliais.data.possuiFiliais) {
            setFiliais(respFiliais.data.opcoes);
            setMostrarFiliais(true);
          }
        }
      } catch (err) {
        if (mounted) setError("Erro ao carregar dados.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
  }, []);

  const podeAprovar = permissao?.podeAprovar === true;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    if (id) {
      setModoEdicao(true);
      setTcaId(id);
      carregarTcaExistente(id);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios
        .get(`${API_BASE_URL}/admin-unidades`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((r) => setUnidadesCieemg(r.data || []))
        .catch(console.error);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios
      .get(`${API_BASE_URL}/tca-configuracoes/horarios-teoricos`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setHorariosTeoricos(r.data || []))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404 || status === 405) return;
        console.warn(
          "[TcaEmpregador] Falha ao carregar horários teóricos:",
          status || "sem status",
          err?.response?.data || err?.message,
        );
      });
  }, []);

  useEffect(() => {
    if (!formData.data_nascimento) {
      setIdadeCalculada(null);
      return;
    }

    const hoje = new Date();
    const nasc = new Date(formData.data_nascimento);
    let idade = hoje.getFullYear() - nasc.getFullYear();
    const m = hoje.getMonth() - nasc.getMonth();
    if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) {
      idade--;
    }
    setIdadeCalculada(idade);
  }, [formData.data_nascimento]);

  useEffect(() => {
    if (modoEdicao) return;

    const local = formData.local_realizacao_atividades || "";

    // 1. Reset se escolher a opção "Selecionar..." (vazio)
    if (local === "") {
      setFormData((prev) => ({
        ...prev,
        endereco_aprendizagem_teorica: "",
      }));
      setTcaFormValue("endereco_aprendizagem_teorica", "", {
        shouldDirty: false,
        shouldTouch: false,
      });
      return;
    }

    // 2. Forçar preenchimento com dados da Matriz se for "MATRIZ"
    if (local === "MATRIZ") {
      let enderecoMatriz = "";
      console.log("[MÁGICA MATRIZ] Disparado! regiao=", formData.regiao, "unidades.length=", unidadesCieemg.length);

      console.log(
        "[MÁGICA MATRIZ] Disparado! regiao=",
        formData.regiao,
        "unidades.length=",
        unidadesCieemg.length,
      );

      if (formData.regiao && unidadesCieemg.length > 0) {
        const unidadeEncontrada = unidadesCieemg.find(
          (u) => String(u.codigo) === String(formData.regiao),
        );
        if (unidadeEncontrada) {
          enderecoMatriz = unidadeEncontrada.endereco;
          console.log("[MÁGICA MATRIZ] Encontrado via regiao:", enderecoMatriz);
        }
      }

      if (!enderecoMatriz && matrizEnderecoTeoricoRef.current) {
        enderecoMatriz = matrizEnderecoTeoricoRef.current;
        console.log(
          "[MÁGICA MATRIZ] Usando fallback (matrizEnderecoTeoricoRef):",
          enderecoMatriz,
        );
      }

      if (enderecoMatriz) {
        console.log("[MÁGICA MATRIZ] Setando form data com:", enderecoMatriz);
        setFormData((prev) => ({
          ...prev,
          endereco_aprendizagem_teorica: enderecoMatriz,
        }));
        setTcaFormValue("endereco_aprendizagem_teorica", enderecoMatriz, {
          shouldDirty: false,
          shouldTouch: false,
        });
      } else {
        console.log(
          "[MÁGICA MATRIZ] Nenhum endereço teórico encontrado (fallback vazio).",
        );
      }
    }
  }, [formData.regiao, unidadesCieemg, formData.local_realizacao_atividades]);

  useEffect(() => {
    const filialId = String(localAtividade || "").trim();
    console.log(
      "[Filial] useEffect disparado. localAtividade =",
      JSON.stringify(localAtividade),
      "filialId =",
      filialId,
    );
    if (!filialId || filialId === "MATRIZ" || isNaN(Number(filialId))) {
      console.log("[Filial] ignorado (vazio / MATRIZ / não-numérico)");
      const camposFilial = [
        "cpfCnpj",
        "cepFilial",
        "enderecoFilial",
        "telefones",
        "telefones2",
        "emailContratos",
        "emailFinanceiro",
        "representadaPor",
        "cargoRepresentante",
        "emailRepresentante",
        "responsavelSupervisor",
        "cargoSupervisor",
        "emailSupervisor",
      ];
      let enderecoMatriz = matrizEnderecoTeoricoRef.current || "";
      if (!enderecoMatriz && formData.regiao && unidadesCieemg.length > 0) {
        const unidadeEncontrada = unidadesCieemg.find(
          (u) =>
            Number(u.codigo) === Number(formData.regiao) ||
            String(u.codigo) === String(formData.regiao) ||
            String(u.id) === String(formData.regiao) ||
            String(u.codigo_unidade) === String(formData.regiao),
        );
        if (unidadeEncontrada) {
          enderecoMatriz = unidadeEncontrada.endereco;
        }
      }
      console.log("[TcaEmpregador] Magica da Matriz:", {
        regiao: formData.regiao,
        enderecoMatriz,
      });

      setFormData((prev) => {
        const next = { ...prev };
        camposFilial.forEach((c) => {
          next[c] = "";
        });
        if (matrizEnderecoTeoricoRef.current) {
          next.endereco_aprendizagem_teorica = matrizEnderecoTeoricoRef.current;
        }
        return next;
      });
      camposFilial.forEach((c) =>
        setTcaFormValue(c, "", { shouldDirty: false, shouldTouch: false }),
      );
      if (matrizEnderecoTeoricoRef.current) {
        setTcaFormValue(
          "endereco_aprendizagem_teorica",
          matrizEnderecoTeoricoRef.current,
          { shouldDirty: false, shouldTouch: false },
        );
      }
      return;
    }

    let cancelled = false;

    const carregarFilial = async () => {
      setFilialLoading(true);
      try {
        const token = localStorage.getItem("token");
        const url = API_ENDPOINTS.filial_id(filialId);
        console.log("[Filial] GET", url);
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log("[Filial] resposta:", response.status, response.data);

        if (cancelled) return;

        const dados = response.data || {};

        const enderecoCompletoFilial = [
          dados.endereco,
          dados.bairro,
          dados.cidade,
          dados.uf,
          dados.cep ? `CEP: ${dados.cep}` : "",
        ]
          .filter(Boolean)
          .join(", ");

        const mapeamento = [
          ["nome_filial", dados.nome_filial],
          ["cpfCnpj", dados.cnpj],
          ["cepFilial", dados.cep],
          ["enderecoFilial", dados.endereco],
          ["telefones", dados.telefone_1],
          ["telefones2", dados.telefone_2],
          [
            "endereco_aprendizagem_teorica",
            dados.unidadeTeorica ? dados.unidadeTeorica.endereco : "",
          ],
          ["bairro", dados.bairro],
          ["municipio", dados.cidade],
          ["uf", dados.uf],
          ["emailContratos", dados.email_contratos],
          ["emailFinanceiro", dados.email_financeiro],
          ["representadaPor", dados.representante_nome],
          ["cargoRepresentante", dados.representante_cargo],
          ["emailRepresentante", dados.representante_email],
          ["responsavelSupervisor", dados.supervisor_nome],
          ["cargoSupervisor", dados.supervisor_cargo],
          ["emailSupervisor", dados.supervisor_email],
        ];

        setFormData((prev) => {
          const next = { ...prev };
          mapeamento.forEach(([campo, valor]) => {
            if (valor !== undefined && valor !== null) {
              next[campo] = valor;
            }
          });
          return next;
        });

        mapeamento.forEach(([campo, valor]) => {
          if (valor !== undefined && valor !== null) {
            setTcaFormValue(campo, valor, {
              shouldDirty: false,
              shouldTouch: false,
            });
          }
        });

        toastSuccess("Dados da filial carregados.");
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        const backendMsg =
          (Array.isArray(err.response?.data?.errors) &&
            err.response.data.errors.join(", ")) ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          null;
        console.error(
          `[Filial] erro ao carregar filial id=${filialId} status=${status}:`,
          err.response?.data,
        );
        if (status === 404) {
          toastError(
            backendMsg
              ? `Filial não encontrada: ${backendMsg}`
              : "Filial não encontrada",
          );
        } else if (status >= 500) {
          toastError(
            backendMsg
              ? `Erro no servidor ao buscar filial: ${backendMsg}`
              : "Erro no servidor ao buscar filial",
          );
        } else if (status === 401 || status === 403) {
          toastError("Sessão expirada ou sem permissão. Faça login novamente.");
        } else {
          toastError(
            backendMsg
              ? `Erro ao carregar filial: ${backendMsg}`
              : "Erro ao carregar dados da filial",
          );
        }
      } finally {
        if (!cancelled) setFilialLoading(false);
      }
    };

    carregarFilial();
    return () => {
      cancelled = true;
    };
  }, [
    localAtividade,
    setTcaFormValue,
    toastError,
    toastSuccess,
    formData.regiao,
    unidadesCieemg,
  ]);

  const handleCepFilialBlur = async (e) => {
    const cepVal = String(e.target.value || "").replace(/\D/g, "");
    if (cepVal.length === 8) {
      try {
        const response = await axios.get(
          `https://viacep.com.br/ws/${cepVal}/json/`,
        );
        if (!response.data.erro) {
          setFormData((prev) => ({
            ...prev,
            enderecoFilial: response.data.logradouro || "",
            bairro: response.data.bairro || "",
            municipio: response.data.localidade || "",
            uf: response.data.uf || "",
          }));
          setTcaFormValue("enderecoFilial", response.data.logradouro || "");
          setTcaFormValue("bairro", response.data.bairro || "");
          setTcaFormValue("municipio", response.data.localidade || "");
          setTcaFormValue("uf", response.data.uf || "");
          toastSuccess("Endereço preenchido automaticamente.");
        } else {
          toastWarning("CEP não encontrado no ViaCEP.");
        }
      } catch (err) {
        toastError("Erro ao buscar CEP no ViaCEP.");
      }
    }
  };

  useEffect(() => {
    const carregarOpcoes = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_BASE_URL}/tca-configuracoes/datas-vigencia`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const ordenadas = (response.data || []).slice().sort((a, b) => {
          const da = a?.valorObj?.data || "";
          const db = b?.valorObj?.data || "";
          return da.localeCompare(db);
        });
        setDatasVigenciaFuturas(ordenadas);
      } catch (err) {
        console.error("Erro ao carregar datas de vigência:", err);
      }
    };
    const buscarSalarioMinimo = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_BASE_URL}/tca-configuracoes/salario-minimo`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (
          response.data &&
          response.data.vigente &&
          response.data.vigente.valor
        ) {
          setSalarioMinimoVigente(Number(response.data.vigente.valor));
        } else if (Array.isArray(response.data) && response.data.length > 0) {
          setSalarioMinimoVigente(
            Number(response.data[response.data.length - 1].valor),
          );
        } else if (response.data && response.data.valor) {
          setSalarioMinimoVigente(Number(response.data.valor));
        }
      } catch (err) {
        console.warn("Erro ao buscar salário mínimo:", err);
      }
    };
    carregarOpcoes();
    buscarSalarioMinimo();
  }, []);

  const prevSalarioCalculadoRef = useRef(null);

  // Calcula o salário proporcional automaticamente com base na carga horária diária
  useEffect(() => {
    let horasDiarias = 0;
    const chd = String(formData.carga_horaria_diaria || "").replace(/\D/g, "");
    if (chd === "4" || formData.carga_horaria_diaria === "4 horas diárias") {
      horasDiarias = 4;
    } else if (
      chd === "6" ||
      formData.carga_horaria_diaria === "6 horas diárias"
    ) {
      horasDiarias = 6;
    }

    if (horasDiarias === 4 || horasDiarias === 6) {
      const valorHora = Math.round((salarioMinimoVigente / 220) * 100) / 100;
      const horasSemanais = horasDiarias * 5;
      const valorSemanal = valorHora * horasSemanais;
      const salarioCalculado =
        Math.round(((valorSemanal * 4.4285 * 7) / 6) * 100) / 100;

      const currentSal = parseSalario(formData.salario);
      if (
        !currentSal ||
        currentSal < salarioCalculado ||
        currentSal === prevSalarioCalculadoRef.current
      ) {
        setFormData((prev) => ({
          ...prev,
          salario: salarioCalculado.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
          }),
        }));
      }

      prevSalarioCalculadoRef.current = salarioCalculado;
    }
  }, [formData.carga_horaria_diaria, salarioMinimoVigente]);

  useEffect(() => {
    if (modoEdicao) return;

    // 1. Reset se escolher a opção "Selecionar..." (vazio)
    if (localAtividade === "") {
      setFormData((prev) => ({
        ...prev,
        endereco_aprendizagem_teorica: "",
      }));
      return;
    }

    // 2. Forçar preenchimento com dados da Matriz se for "MATRIZ"
    if (localAtividade === "MATRIZ") {
      if (formData.regiao && unidadesCieemg.length > 0) {
        const unidadeEncontrada = unidadesCieemg.find(
          (u) => String(u.codigo) === String(formData.regiao),
        );

        if (unidadeEncontrada) {
          setFormData((prev) => ({
            ...prev,
            endereco_aprendizagem_teorica: unidadeEncontrada.endereco,
          }));
        }
      }
    }
  }, [formData.regiao, unidadesCieemg, localAtividade, modoEdicao]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = value;

    // Bloqueia edição de campos pessoais/admissão do aprendiz para a empresa.
    if (isCampoBloqueadoParaEmpresa(name)) {
      toastWarning(
        "Este campo é de responsabilidade do estudante e não pode ser alterado pela empresa.",
      );
      return;
    }

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === "data_inicio_contrato") {
    }

    const emojiRegex = /[\p{Extended_Pictographic}]/gu;
    const camposSemEmoji = [
      "cnpj_ou_cpf",
      "cpf",
      "cpf_mae",
      "cpf_declarante_ir",
      "dependente_1_cpf",
      "dependente_2_cpf",
      "dependente_3_cpf",
      "telefone_empresa",
      "telefone_representante_legal",
      "telefone_mae",
      "telefone_01",
      "telefone_02",
      "rg",
      "pis",
      "titulo_eleitor",
      "zona",
      "secao",
      "numero_certificado_reservista",
      "agencia",
      "div1",
      "conta",
      "div2",
      "cep",
      "data_nascimento",
      "data_nascimento_mae",
      "data_emissao_rg",
      "data_emissao_titulo",
      "data_emissao_certificado",
      "dependente_1_data_nascimento",
      "dependente_2_data_nascimento",
      "dependente_3_data_nascimento",
      "data_inicio_contrato",
      "carga_horaria_diaria",
      "salario",
      "horario_pratica_inicio",
      "horario_pratica_fim",
      "horario_teorica_inicio",
      "horario_teorica_fim",
    ];
    if (!camposSemEmoji.includes(name)) {
      newValue = newValue.replace(emojiRegex, "");
    }

    const regexLetras = /[^0-9a-zA-ZÀ-ÿ\s.,&\-/]/g;
    const camposLetras = [
      "razao_social",
      "nome_representante_legal",
      "cargo_representante_legal",
      "tutor_aprendiz_empresa",
      "cargo_tutor",
      "nome_tutor",
      "endereco_aprendizagem_pratica",
      "endereco_residencial_vt",
      "servicos_meios_transporte",
      "linha_pratica",
      "linha_teorica",
      "nome_completo",
      "nome_social",
      "estado_natal",
      "naturalidade",
      "nacionalidade",
      "cor",
      "nome_mae",
      "naturalidade_mae",
      "estado_civil_mae",
      "logradouro",
      "bairro",
      "cidade",
      "estado_endereco",
      "pais",
      "grau_instrucao",
      "curso_escolaridade",
      "instituicao_ensino",
      "conclusao",
      "orgao_emissor_rg",
      "estado_rg",
      "orgao_expedicao",
      "situacao_militar",
      "banco_pagamento",
      "risco_exposto",
      "nome_declarante_ir",
      "estado_civil_ir",
      "dependente_1_nome",
      "dependente_2_nome",
      "dependente_3_nome",
      "dependente_1_grau_parentesco",
      "dependente_2_grau_parentesco",
      "dependente_3_grau_parentesco",
      "dependente_1_naturalidade",
      "dependente_2_naturalidade",
      "dependente_3_naturalidade",
      "observacoes_ajuste_contrato",
    ];
    if (camposLetras.includes(name)) {
      newValue = newValue.replace(regexLetras, "");
    }

    const regexNumeros = /[^0-9]/g;
    const camposNumerosPuros = [
      "pis",
      "titulo_eleitor",
      "zona",
      "secao",
      "numero_certificado_reservista",
      "agencia",
      "div1",
      "div2",
      "conta",
      "qtd_dia_pratica",
      "qtd_dia_teorica",
      "carga_horaria_diaria",
    ];
    if (camposNumerosPuros.includes(name)) {
      newValue = newValue.replace(regexNumeros, "");
    }
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const buildReviewData = () => {
    const cpfLimpo = (formData.cpf || "").replace(/\D/g, "");

    const empregadorFields = [
      {
        label: "Nome do representante legal",
        value: formData.nome_representante_legal,
        complete: !!formData.nome_representante_legal?.trim(),
      },
      {
        label: "Cargo",
        value: formData.cargo_representante_legal,
        complete: !!formData.cargo_representante_legal?.trim(),
      },
      {
        label: "Data início contrato",
        value: formatDateBR(formData.data_inicio_contrato),
        complete: !!formData.data_inicio_contrato,
      },
      {
        label: "Salário",
        value: formData.salario,
        complete: !!formData.salario && formData.salario !== "R$ 0,00",
      },
    ];

    if (formData.telefone_empresa?.trim()) {
      empregadorFields.push({
        label: "Telefone Empresa",
        value: formData.telefone_empresa,
        complete: true,
      });
    }
    if (formData.email_empresa?.trim()) {
      empregadorFields.push({
        label: "E-mail Empresa",
        value: formData.email_empresa,
        complete: true,
      });
    }
    if (
      formData.nome_tutor?.trim() ||
      formData.tutor_aprendiz_empresa?.trim()
    ) {
      empregadorFields.push({
        label: "Tutor do Aprendiz",
        value: formData.nome_tutor || formData.tutor_aprendiz_empresa,
        complete: true,
      });
    }
    if (formData.email_tutor?.trim()) {
      empregadorFields.push({
        label: "E-mail do Tutor",
        value: formData.email_tutor,
        complete: true,
      });
    }

    const aprendizFields = [
      {
        label: "Curso",
        value: formData.curso,
        complete: !!formData.curso?.trim(),
      },
      {
        label: "Nome completo",
        value: formData.nome_completo,
        complete: !!formData.nome_completo?.trim(),
      },
      {
        label: "CPF (11 dígitos)",
        value: formData.cpf,
        complete: cpfLimpo.length === 11,
      },
      {
        label: "Data de nascimento",
        value: formatDateBR(formData.data_nascimento),
        complete: !!formData.data_nascimento,
      },
      // Nome da mãe: responsabilidade do estudante — não exibir no review
      // para não mostrar "Não preenchido" para a empresa.
    ];

    if (formData.rg?.trim()) {
      aprendizFields.push({ label: "RG", value: formData.rg, complete: true });
    }
    if (formData.pis?.trim()) {
      aprendizFields.push({
        label: "PIS",
        value: formData.pis,
        complete: true,
      });
    }
    if (formData.telefone_01?.trim()) {
      aprendizFields.push({
        label: "Telefone do Aprendiz",
        value: formData.telefone_01,
        complete: true,
      });
    }
    if (formData.email_pessoal?.trim()) {
      aprendizFields.push({
        label: "E-mail Pessoal",
        value: formData.email_pessoal,
        complete: true,
      });
    }
    if (formData.cep?.trim()) {
      aprendizFields.push({
        label: "CEP",
        value: formData.cep,
        complete: true,
      });
    }
    if (formData.logradouro?.trim()) {
      const endCompleto = `${formData.logradouro}, ${formData.numero_endereco || "S/N"}${formData.complemento ? " (" + formData.complemento + ")" : ""} - ${formData.bairro || ""}, ${formData.cidade || ""}/${formData.estado_endereco || ""}`;
      aprendizFields.push({
        label: "Endereço",
        value: endCompleto,
        complete: true,
      });
    }
    if (formData.grau_instrucao?.trim()) {
      aprendizFields.push({
        label: "Escolaridade",
        value: formData.grau_instrucao,
        complete: true,
      });
    }
    if (formData.curso_escolaridade?.trim()) {
      aprendizFields.push({
        label: "Curso Escolaridade",
        value: formData.curso_escolaridade,
        complete: true,
      });
    }
    if (formData.instituicao_ensino?.trim()) {
      aprendizFields.push({
        label: "Instituição de Ensino",
        value: formData.instituicao_ensino,
        complete: true,
      });
    }

    return [
      {
        id: "empregador",
        section: "Dados do Empregador",
        fields: empregadorFields,
      },
      {
        id: "aprendiz",
        section: "Dados do Aprendiz",
        fields: aprendizFields,
      },
      {
        id: "local",
        section: "Local de Atividade",
        fields: [
          {
            label: "Local de realização das atividades",
            value:
              localAtividade === "FILIAL"
                ? "Filial"
                : localAtividade || "MATRIZ",
            complete: !mostrarFiliais || !!localAtividade,
          },
        ],
      },
      ...(mostrarFiliais && localAtividade && localAtividade !== "MATRIZ"
        ? [
            {
              id: "filial",
              section: "Informações da Filial",
              fields: [
                {
                  label: "Nome da Filial",
                  value: formData.nome_filial,
                  complete: !!formData.nome_filial?.trim(),
                },
                {
                  label: "CPF/CNPJ",
                  value: formData.cpfCnpj,
                  complete: !!formData.cpfCnpj?.trim(),
                },
                // Bairro/Distrito: já é exibido no Endereço completo abaixo.
                {
                  label: "Município",
                  value: formData.municipio,
                  complete: !!formData.municipio?.trim(),
                },
                {
                  label: "UF",
                  value: formData.uf,
                  complete: !!formData.uf?.trim(),
                },
                {
                  label: "Telefone 1",
                  value: formData.telefones,
                  complete: !!formData.telefones?.trim(),
                },
                {
                  label: "Telefone 2",
                  value: formData.telefones2,
                  complete: true, // Opcional
                },
                {
                  label: "E-mail Contrato",
                  value: formData.emailContratos,
                  complete: !!formData.emailContratos?.trim(),
                },
                {
                  label: "E-mail Financeiro",
                  value: formData.emailFinanceiro,
                  complete: !!formData.emailFinanceiro?.trim(),
                },
                {
                  label: "Representante por",
                  value: formData.representadaPor,
                  complete: !!formData.representadaPor?.trim(),
                },
                {
                  label: "Cargo do Representante",
                  value: formData.cargoRepresentante,
                  complete: !!formData.cargoRepresentante?.trim(),
                },
                {
                  label: "E-mail do Representante",
                  value: formData.emailRepresentante,
                  complete: !!formData.emailRepresentante?.trim(),
                },
                {
                  label: "Supervisor do Estágio",
                  value: formData.responsavelSupervisor,
                  complete: !!formData.responsavelSupervisor?.trim(),
                },
                {
                  label: "Cargo do Supervisor",
                  value: formData.cargoSupervisor,
                  complete: !!formData.cargoSupervisor?.trim(),
                },
                {
                  label: "E-mail do Supervisor",
                  value: formData.emailSupervisor,
                  complete: !!formData.emailSupervisor?.trim(),
                },
              ],
            },
          ]
        : []),
      ...(formData.banco_pagamento || formData.conta
        ? [
            {
              id: "bancario",
              section: "Dados Bancários",
              fields: [
                ...(formData.banco_pagamento
                  ? [
                      {
                        label: "Banco",
                        value: formData.banco_pagamento,
                        complete: true,
                      },
                    ]
                  : []),
                ...(formData.agencia
                  ? [
                      {
                        label: "Agência",
                        value: `${formData.agencia}${formData.div1 ? "-" + formData.div1 : ""}`,
                        complete: true,
                      },
                    ]
                  : []),
                ...(formData.conta
                  ? [
                      {
                        label: "Conta",
                        value: `${formData.conta}${formData.div2 ? "-" + formData.div2 : ""}`,
                        complete: true,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      ...(formData.endereco_residencial_vt || formData.servicos_meios_transporte
        ? [
            {
              id: "vt",
              section: "Vale Transporte",
              fields: [
                ...(formData.endereco_residencial_vt
                  ? [
                      {
                        label: "Endereço Residencial VT",
                        value: formData.endereco_residencial_vt,
                        complete: true,
                      },
                    ]
                  : []),
                ...(formData.servicos_meios_transporte
                  ? [
                      {
                        label: "Meios de Transporte",
                        value: formData.servicos_meios_transporte,
                        complete: true,
                      },
                    ]
                  : []),
                ...(formData.linha_pratica
                  ? [
                      {
                        label: "Linha Prática",
                        value: formData.linha_pratica,
                        complete: true,
                      },
                    ]
                  : []),
                ...(formData.tarifa_pratica
                  ? [
                      {
                        label: "Tarifa Prática",
                        value: formData.tarifa_pratica,
                        complete: true,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      ...(formData.dependente_1_nome ||
      formData.dependente_2_nome ||
      formData.dependente_3_nome
        ? [
            {
              id: "dependentes",
              section: "Dependentes para IR",
              fields: [
                ...(formData.dependente_1_nome
                  ? [
                      {
                        label: "Dependente 1",
                        value: formData.dependente_1_nome,
                        complete: true,
                      },
                      {
                        label: "CPF Dep 1",
                        value: formData.dependente_1_cpf || "Não informado",
                        complete: true,
                      },
                    ]
                  : []),
                ...(formData.dependente_2_nome
                  ? [
                      {
                        label: "Dependente 2",
                        value: formData.dependente_2_nome,
                        complete: true,
                      },
                      {
                        label: "CPF Dep 2",
                        value: formData.dependente_2_cpf || "Não informado",
                        complete: true,
                      },
                    ]
                  : []),
                ...(formData.dependente_3_nome
                  ? [
                      {
                        label: "Dependente 3",
                        value: formData.dependente_3_nome,
                        complete: true,
                      },
                      {
                        label: "CPF Dep 3",
                        value: formData.dependente_3_cpf || "Não informado",
                        complete: true,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
    ];
  };

  const openReview = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();

    const missingFields = buildMissingFields();
    if (missingFields.length > 0) {
      setMissingFieldsAlert(missingFields);
      setShowReview(false);
      return;
    }

    setMissingFieldsAlert([]);
    setShowReview(true);
  };

  const buildMissingFields = () => {
    const items = [];
    if (!formData.nome_representante_legal?.trim())
      items.push({
        id: "nome_representante_legal",
        label: "Nome do representante legal",
      });
    if (!formData.cargo_representante_legal?.trim())
      items.push({ id: "cargo_representante_legal", label: "Cargo" });
    if (!formData.data_inicio_contrato)
      items.push({ id: "data_inicio_contrato", label: "Data início contrato" });
    if (!formData.salario || formData.salario === "R$ 0,00")
      items.push({ id: "salario", label: "Salário" });
    if (!formData.curso?.trim()) items.push({ id: "curso", label: "Curso" });
    if (!formData.nome_completo?.trim())
      items.push({ id: "nome_completo", label: "Nome completo do aprendiz" });
    if (!formData.cpf || formData.cpf.replace(/\D/g, "").length !== 11)
      items.push({ id: "cpf", label: "CPF do aprendiz (11 dígitos)" });
    if (!formData.data_nascimento)
      items.push({ id: "data_nascimento", label: "Data de nascimento" });

    if (mostrarFiliais && !localAtividade)
      items.push({
        id: "localAtividade",
        label: "Local de realização das atividades",
      });
    return items;
  };

  const handleMissingFieldClick = (fieldId) => {
    setMissingFieldsAlert([]);
    setTimeout(() => {
      const el =
        document.getElementById(fieldId) ||
        document.querySelector(`[name="${fieldId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") {
          try {
            el.focus({ preventScroll: true });
          } catch (_) {
            el.focus();
          }
        }
      }
    }, 150);
  };

  const handleEditSection = (sectionId) => {
    setShowReview(false);
    setTimeout(() => {
      const el = document.getElementById(`tca-section-${sectionId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 200);
  };

  const handleConfirmSubmit = async () => {
    setShowReview(false);
    await handleSubmitEmpregador();
  };

  const handleSubmitEmpregador = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (e && e.stopPropagation) {
      e.stopPropagation();
    }

    if (submittingRef.current) return;

    submittingRef.current = true;

    const erros = [];
    if (!formData.nome_representante_legal?.trim())
      erros.push("Nome do representante legal");
    if (!formData.cargo_representante_legal?.trim()) erros.push("Cargo");
    if (!formData.data_inicio_contrato) erros.push("Data início contrato");
    if (!formData.salario || formData.salario === "R$ 0,00")
      erros.push("Salário");
    if (!formData.curso?.trim()) erros.push("Curso");
    if (!formData.nome_completo?.trim())
      erros.push("Nome completo do aprendiz");
    if (!formData.cpf || formData.cpf.replace(/\D/g, "").length !== 11)
      erros.push("CPF do aprendiz (11 dígitos)");
    if (!formData.data_nascimento) erros.push("Data de nascimento");
    // nome_mae: responsabilidade do estudante — não bloquear submit da empresa.
    // Bairro/Distrito: responsabilidade do estudante — não bloquear submit.
    if (mostrarFiliais && !localAtividade)
      erros.push("Local de realização das atividades");

    if (erros.length > 0) {
      const missingFields = buildMissingFields();
      setMissingFieldsAlert(missingFields);
      submittingRef.current = false;
      return;
    }

    let horasDiarias = 0;
    const chd = String(formData.carga_horaria_diaria || "").replace(/\D/g, "");
    if (chd === "4" || formData.carga_horaria_diaria === "4 horas diárias") {
      horasDiarias = 4;
    } else if (
      chd === "6" ||
      formData.carga_horaria_diaria === "6 horas diárias"
    ) {
      horasDiarias = 6;
    } else if (formData.curso && cursosFiltrados.length > 0) {
      const cursoObj = cursosFiltrados.find(
        (c) => c.valorObj.curso === formData.curso,
      );
      if (cursoObj && cursoObj.valorObj.carga_horaria) {
        const ch = String(cursoObj.valorObj.carga_horaria).replace(/\D/g, "");
        if (ch === "1936" || ch === "1900") horasDiarias = 4;
        else if (ch === "2230" || ch === "2000") horasDiarias = 6;
      }
    }

    if (horasDiarias === 4 || horasDiarias === 6) {
      const valorHora = Math.round((salarioMinimoVigente / 220) * 100) / 100;
      const horasSemanais = horasDiarias * 5;
      const valorSemanal = valorHora * horasSemanais;
      const salarioMinimoCalculado =
        Math.round(((valorSemanal * 4.4285 * 7) / 6) * 100) / 100;

      const salarioInformado = parseSalario(formData.salario);
      if (salarioInformado < salarioMinimoCalculado) {
        toastError(
          `O salário informado (R$ ${salarioInformado.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}) é inferior ao salário mínimo vigente de R$ ${salarioMinimoCalculado.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} para a carga horária de ${horasDiarias} horas.`,
        );
        submittingRef.current = false;
        return;
      }
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));

      let linkAssinatura = LINK_DOCUSIGN_TCA_CIEEMG_FALLBACK;
      if (user.empresa_id) {
        try {
          const autorizacao = await getAutorizacao(user.empresa_id);
          if (autorizacao?.linkDocuSign)
            linkAssinatura = autorizacao.linkDocuSign;
        } catch (err) {
          console.warn("Erro ao buscar autorização:", err);
        }
      }

      const isEdicao = !!formData.id;
      const payloadBase = { ...formData };
      if (Number(user?.acesso_autorizacao) === 0) {
        CAMPOS_APRENDIZ_BLOQUEADOS_EMPRESA.forEach((k) => {
          delete payloadBase[k];
        });
        // TEMPORÁRIO PARA TESTE: re-injeta nome_mae e bairro como string vazia
        // para que o backend receba os campos (mesmo que opcionais).
        // REMOVER quando o backend for ajustado para aceitar esses campos ausentes.
        payloadBase.nome_mae = formData.nome_mae || "";
        payloadBase.bairro = formData.bairro || "";
      }

      const payload = {
        ...payloadBase,
        cidade: formData.municipio || formData.cidade,
        email_contrato: formData.emailContratos,
        email_financeiro: formData.emailFinanceiro,
        cnpj_ou_cpf_filial: formData.cpfCnpj,
        nomecontato: formData.representadaPor,
        cargo: formData.cargoRepresentante,
        email: formData.emailRepresentante,
        nomecontato2: formData.responsavelSupervisor,
        cargo2: formData.cargoSupervisor,
        email1: formData.emailSupervisor,
        empresa_id: user.empresa_id,
        tipo_programa: 2,
        tipo_autorizacao:
          autorizacaoTca?.tipoAutorizacao || autorizacaoTca?.tipo,
        link_docusign: linkAssinatura,
        local_realizacao_atividades: localAtividade || "MATRIZ",
        local_execucao_diferente: formData.local_execucao_diferente ? 1 : 0,
        fumante: formData.fumante ? 1 : 0,
        deseja_vale_transporte: formData.deseja_vale_transporte ? 1 : 0,
        trabalhou_ambiente_insalubre: formData.trabalhou_ambiente_insalubre
          ? 1
          : 0,
      };
      const response = isEdicao
        ? await axios.put(
            `${API_BASE_URL}/tca-empregador/${formData.id}`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } },
          )
        : await axios.post(`${API_BASE_URL}/tca-empregador`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });

      const filialIdNum = Number(localAtividade);
      const filialPayload = {
        nome_filial: formData.nome_filial,
        cnpj: formData.cpfCnpj,
        cep: formData.cepFilial,
        endereco: formData.enderecoFilial,
        telefone_1: formData.telefones,
        telefone_2: formData.telefones2,
        bairro: formData.bairro,
        cidade: formData.municipio,
        uf: formData.uf,
        email_contratos: formData.emailContratos,
        email_financeiro: formData.emailFinanceiro,
        representante_nome: formData.representadaPor,
        representante_cargo: formData.cargoRepresentante,
        representante_email: formData.emailRepresentante,
        supervisor_nome: formData.responsavelSupervisor,
        supervisor_cargo: formData.cargoSupervisor,
        supervisor_email: formData.emailSupervisor,
      };
      console.log(
        "[Filial][PUT] localAtividade =",
        localAtividade,
        "filialIdNum =",
        filialIdNum,
        "isNaN =",
        isNaN(filialIdNum),
      );
      if (
        localAtividade &&
        localAtividade !== "MATRIZ" &&
        !isNaN(filialIdNum)
      ) {
        try {
          console.log(
            "[Filial][PUT] enviando PUT /filiais/" + filialIdNum,
            filialPayload,
          );
          const filialResp = await axios.put(
            API_ENDPOINTS.filial_update(filialIdNum),
            filialPayload,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          console.log(
            "[Filial][PUT] resposta:",
            filialResp.status,
            filialResp.data,
          );
          toastSuccess("Dados da filial atualizados.");
        } catch (filialErr) {
          console.warn(
            "Falha ao persistir filial (PUT /filiais/:id):",
            filialErr,
            filialErr?.response?.data,
          );
          const status = filialErr.response?.status;
          const backendMsg =
            filialErr.response?.data?.message ||
            filialErr.response?.data?.erro ||
            filialErr.response?.data?.error ||
            filialErr.message ||
            "Erro ao atualizar dados da filial";
          if (status === 403) {
            const acessos = filialErr.response?.data?.acessos_necessarios;
            const acessoTxt = Array.isArray(acessos)
              ? ` (níveis necessários: ${acessos.join(", ")})`
              : "";
            toastError(
              `Sem permissão para atualizar a filial${acessoTxt}. Contate o administrador do sistema.`,
            );
          } else if (status === 401) {
            toastError(
              "Sessão expirada. Faça login novamente para atualizar a filial.",
            );
          } else if (status === 404) {
            toastError("Filial não encontrada no servidor.");
          } else if (status >= 500) {
            toastError(`Erro no servidor ao atualizar filial: ${backendMsg}`);
          } else {
            toastError(backendMsg);
          }
        }
      } else {
        console.warn(
          "[Filial][PUT] NÃO enviado — localAtividade =",
          localAtividade,
          "filialIdNum =",
          filialIdNum,
        );
      }

      const novoTcaId = response.data?.id || response.data?.tcaId || "?";

      const abrir = await confirm({
        title: "TCA CIEEMG enviado com sucesso!",
        message: `TCA #${novoTcaId} foi enviado.\n\nDeseja abrir o DocuSign agora para assinar o contrato?`,
        confirmText: "Abrir DocuSign",
        cancelText: "Depois",
        variant: "success",
      });

      if (abrir) {
        window.open(linkAssinatura, "_blank", "noopener,noreferrer");
      } else {
        toastSuccess(
          "TCA salvo. Você pode abrir o DocuSign depois pelo botão na tela.",
        );
      }

      setSuccess(true);
    } catch (error) {
      console.error("ERRO AO SALVAR TCA CIEEMG:", error);

      toastError(
        error.response?.data?.errors?.[0] || "Erro ao salvar TCA CIEEMG",
      );
    } finally {
      setSubmitting(false);

      submittingRef.current = false;
    }
  };

  const aprovarTcaEmpregador = async () => {
    const ok = await confirm({
      title: "Aprovar TCA",
      message:
        "Tem certeza que deseja APROVAR este TCA?\nEsta ação não pode ser desfeita.",
      confirmText: "Sim, aprovar",
      cancelText: "Cancelar",
      variant: "success",
    });
    if (!ok) return;
    const tcaId = formData.id;
    if (!tcaId) {
      toastError("ID do TCA não encontrado.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tca-empregador/${tcaId}/aprovar`,
        { observacao: "Aprovado por SECEX" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toastSuccess("TCA aprovado com sucesso!");
      if (carregarTcaExistente) carregarTcaExistente(tcaId);
    } catch (err) {
      console.error("Erro ao aprovar:", err);
      toastError(
        err.response?.data?.errors?.[0] || err.message || "Erro ao aprovar",
      );
    }
  };

  const recusarTcaEmpregador = async () => {
    const obs = await promptUser({
      title: "Recusar TCA",
      message: "Informe o motivo da recusa:",
      inputPlaceholder: "Ex: documentos incompletos...",
      confirmText: "Recusar TCA",
      cancelText: "Cancelar",
      variant: "danger",
    });
    if (!obs) return;
    const tcaId = formData.id;
    if (!tcaId) {
      toastError("ID do TCA não encontrado.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tca-empregador/${tcaId}/reprovar`,
        { observacao: obs },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toastSuccess("TCA recusado!");
      if (carregarTcaExistente) carregarTcaExistente(tcaId);
    } catch (err) {
      console.error("Erro ao recusar:", err);
      toastError(
        err.response?.data?.errors?.[0] || err.message || "Erro ao recusar",
      );
    }
  };



  if (loading)
    return (
      <div className="tca-container tca-py-5 tca-text-center">Carregando…</div>
    );

  async function carregarTcaExistente(id) {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/tca-empregador/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const dados = { ...response.data };
      const camposData = [
        "data_emissao_rg",
        "data_emissao_titulo",
        "data_emissao_certificado",
        "data_nascimento",
        "data_nascimento_mae",
        "dependente_1_data_nascimento",
        "dependente_2_data_nascimento",
        "dependente_3_data_nascimento",
        "data_inicio_contrato",
      ];
      camposData.forEach((campo) => {
        if (dados[campo]) {
          const d = new Date(dados[campo]);
          if (!isNaN(d.getTime())) {
            dados[campo] =
              `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          }
        }
      });

      if (
        dados.local_execucao_diferente !== undefined &&
        dados.local_execucao_diferente !== null
      ) {
        dados.local_execucao_diferente = Boolean(
          Number(dados.local_execucao_diferente),
        );
      }

      if (
        dados.salario !== null &&
        dados.salario !== undefined &&
        dados.salario !== ""
      ) {
        const num =
          typeof dados.salario === "string"
            ? parseFloat(dados.salario)
            : dados.salario;
        if (!isNaN(num)) {
          dados.salario = num.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          });
        }
      }
      setTcaStatus(dados.status);
      // Ao carregar um TCA existente, sobrescreve TODOS os campos com o
      // que veio do backend — é a fonte da verdade. Os campos pessoais/
      // admissão do aprendiz são responsabilidade do estudante e da
      // empresa, mas a leitura vem do banco.
      setFormData((prev) => {
        const merged = { ...prev, ...dados };
        return merged;
      });
      setTcaHistorico(dados.historico || []);
    } catch (err) {
      console.error("Erro ao carregar TCA:", err);
      const status = err?.response?.status;
      const backendMsg =
        err?.response?.data?.errors?.[0] ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Erro ao carregar TCA existente.";
      toastError(
        status
          ? `Erro ${status} ao carregar TCA: ${backendMsg}`
          : `Erro ao carregar TCA: ${backendMsg}`,
      );
    }
  }

  async function aprovar() {
    if (!tcaId) return;
    const observacao = prompt("Observação da aprovação (opcional):");
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tca-empregador/${tcaId}/aprovar-validacao`,
        { observacao: observacao || "Aprovado" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toastSuccess("TCA Empregador aprovado!");
      carregarTcaExistente(tcaId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro ao aprovar");
    }
  }

  async function recusar() {
    if (!tcaId) return;
    const observacao = prompt("Motivo da recusa:");
    if (!observacao) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/tca-empregador/${tcaId}/reprovar-validacao`,
        { observacao },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toastWarning("TCA Empregador reprovado!");
      carregarTcaExistente(tcaId);
    } catch (err) {
      toastError(err.response?.data?.errors?.[0] || "Erro ao reprovar");
    }
  }

  const abrirDocuSign = () => {
    if (!linkDocuSign) {
      toastError("Link do DocuSign não disponível. Contate o suporte.");
      return;
    }

    let url = String(linkDocuSign).trim();

    if (!url.match(/^https?:\/\//i)) {
      console.warn("Link DocuSign inválido, usando fallback");
      url = LINK_DOCUSIGN_TCA_CIEEMG_FALLBACK;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getStatusCor = (status) => {
    const s = String(status || "").toUpperCase();

    if (s === "INÍCIO" || s === "INICIO") return "#005ac2";
    if (s.includes("APROVADO") || s.includes("APROVAR")) return "#16a34a";
    if (
      s.includes("REJEITADO") ||
      s.includes("REPROVADO") ||
      s.includes("RECUSADO") ||
      s.includes("REJEITAR") ||
      s.includes("REPROVAR")
    )
      return "#dc2626";
    if (
      s.includes("PENDENTE") ||
      s.includes("AGUARDANDO") ||
      s.includes("EM_VALIDACAO") ||
      s.includes("CONTRATO_CONFECCIONADO") ||
      s.includes("DISPONIBILIZADO")
    )
      return "#eab308";

    return "#3b82f6";
  };

  const formatHorarioTeoricoLabel = (h) => {
    const inicio = h?.horario_inicio;
    const fim = h?.horario_fim;
    if (!inicio || !fim) return "";
    return `${inicio} às ${fim}`;
  };

  const adicionarDependente = () => {
    if (dependentesList.length < 10) {
      setDependentesList((prev) => {
        let proximo = 1;
        while (prev.includes(proximo)) {
          proximo++;
        }
        return [...prev, proximo];
      });
    } else {
      toastWarning("Máximo de 10 dependentes permitido.");
    }
  };

  const removerDependente = (numero) => {
    setDependentesList(dependentesList.filter((n) => n !== numero));
    setFormData((prev) => ({
      ...prev,
      [`dependente_${numero}_nome`]: "",
      [`dependente_${numero}_cpf`]: "",
      [`dependente_${numero}_data_nascimento`]: "",
      [`dependente_${numero}_idade`]: "",
      [`dependente_${numero}_sexo`]: "",
      [`dependente_${numero}_grau_parentesco`]: "",
      [`dependente_${numero}_naturalidade`]: "",
    }));
  };

  const renderDependente = (numero, onRemover) => {
    const bloqueado = isCampoBloqueadoParaEmpresa(
      `dependente_${numero}_nome`,
    );
    return (
      <div
        className="tca-card tca-mb-4"
      >
        <div
          className="div-dados-empresa-title"
          style={{
            margin: "0",
            borderRadius: "12px 12px 0 0",
            position: "relative"
          }}
        >
          <span style={{ fontSize: "1.05rem", fontWeight: "700" }}>DEPENDENTE {numero}</span>
          {!bloqueado && (
            <button
              type="button"
              onClick={onRemover}
              title="Remover dependente"
              style={{
                position: "absolute",
                right: "1.5rem",
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(239, 68, 68, 0.15)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                color: "#ef4444",
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#ef4444";
                e.currentTarget.style.color = "white";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(239, 68, 68, 0.15)";
                e.currentTarget.style.color = "#ef4444";
              }}
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
        <div className="tca-row" style={{ padding: "1.5rem" }}>
          <div className="tca-col-md-5 tca-mb-3">
            <label>Nome</label>
            <input
              className="tca-form-control"
              name={`dependente_${numero}_nome`}
              value={formData[`dependente_${numero}_nome`] || ""}
              onChange={handleChange}
              readOnly={bloqueado}
            />
          </div>
          <div className="tca-col-md-3 tca-mb-3">
            <label>CPF</label>
            <InputMask
              mask="999.999.999-99"
              maskChar=" "
              className="tca-form-control"
              name={`dependente_${numero}_cpf`}
              value={formData[`dependente_${numero}_cpf`] || ""}
              onChange={handleChange}
              disabled={bloqueado}
            />
          </div>
          <div className="tca-col-md-4 tca-mb-3">
            <label>Data de Nascimento</label>
            <input
              type="date"
              className="tca-form-control"
              name={`dependente_${numero}_data_nascimento`}
              value={formData[`dependente_${numero}_data_nascimento`] || ""}
              onChange={handleChange}
              readOnly={bloqueado}
            />
          </div>
          <div className="tca-col-md-2 tca-mb-3">
            <label>Idade</label>
            <input
              className="tca-form-control"
              name={`dependente_${numero}_idade`}
              value={formData[`dependente_${numero}_idade`] || ""}
              onChange={handleChange}
              readOnly={bloqueado}
            />
          </div>
          <div className="tca-col-md-3 tca-mb-3">
            <label>Sexo</label>
            <select
              className="tca-form-control"
              name={`dependente_${numero}_sexo`}
              value={formData[`dependente_${numero}_sexo`] || ""}
              onChange={handleChange}
              disabled={bloqueado}
            >
              <option value="">Selecione</option>
              <option value="M">Masculino</option>
              <option value="F">Feminino</option>
            </select>
          </div>
          <div className="tca-col-md-3 tca-mb-3">
            <label>Grau de Parentesco</label>
            <input
              className="tca-form-control"
              name={`dependente_${numero}_grau_parentesco`}
              value={formData[`dependente_${numero}_grau_parentesco`] || ""}
              onChange={handleChange}
              readOnly={bloqueado}
            />
          </div>
          <div className="tca-col-md-4 tca-mb-3">
            <label>Naturalidade</label>
            <input
              className="tca-form-control"
              name={`dependente_${numero}_naturalidade`}
              value={formData[`dependente_${numero}_naturalidade`] || ""}
              onChange={handleChange}
              readOnly={bloqueado}
            />
          </div>
        </div>
      </div>
    );
  };

  const cidadeAtualEmpregador = formData.municipio || formData.cidade;

  const horariosTeoricosDisponiveis = cidadeAtualEmpregador
    ? horariosTeoricos.filter((h) => {
        const cidadeNorm = String(cidadeAtualEmpregador).trim().toUpperCase();
        return (h.municipios || []).some(
          (m) => String(m).trim().toUpperCase() === cidadeNorm,
        );
      })
    : [];

  return (
    <div className="tca-container tca-py-4 tca-ci-eemg-page tca-ci-eemg-page-fade">
      <h2 className="tca-h2">TCA — Programa Aprendiz CIEE/MG Empregador</h2>
      {success && (
        <div className="tca-alert tca-alert-success">
          <p>
            <strong>✅ TCA CIEEMG enviado com sucesso!</strong>
          </p>
          <p>
            Clique no botão abaixo para abrir o DocuSign e assinar o contrato:
          </p>

          <button
            type="button"
            onClick={abrirDocuSign}
            className="tca-btn tca-btn-primary"
            style={{ marginTop: "0.5rem", display: "inline-block" }}
          >
            📄 Abrir DocuSign para Assinatura
          </button>
        </div>
      )}

      {error && <div className="tca-alert tca-alert-danger">{error}</div>}

      <form className="tca-ci-eemg-form" onSubmit={(e) => e.preventDefault()}>
        <section className="tca-card tca-mb-4" id="tca-section-empregador">
          <div className="tca-card-header">
            1. Dados da Empresa + Representante + Aprendizagem
          </div>
          <div className="tca-card-body">
            <div className="tca-row">
              <div className="tca-col-md-8 tca-mb-3">
                <label>Razão Social</label>
                <input
                  className="tca-form-control"
                  name="razao_social"
                  value={formData.razao_social}
                  onChange={handleChange}
                  readOnly={isEmpresaReadOnly}
                  required
                />
              </div>

              <div className="tca-col-md-4 tca-mb-3">
                <label>CNPJ/CPF</label>
                <input
                  className="tca-form-control"
                  name="cnpj_ou_cpf"
                  value={formData.cnpj_ou_cpf}
                  onChange={handleChange}
                  readOnly={isEmpresaReadOnly}
                  required
                />
              </div>

              <div className="tca-col-md-4 tca-mb-3">
                <label>Telefone</label>
                <input
                  className="tca-form-control"
                  name="telefone_empresa"
                  value={formData.telefone_empresa}
                  onChange={handleChange}
                  readOnly={isEmpresaReadOnly}
                />
              </div>

              <div className="tca-col-md-8 tca-mb-3">
                <label>E-mail</label>
                <input
                  type="email"
                  className="tca-form-control"
                  name="email_empresa"
                  value={formData.email_empresa}
                  onChange={handleChange}
                  readOnly={isEmpresaReadOnly}
                />
              </div>

              {permissao?.podeEditarEmpresa === true && (
                <>
                  <div
                    className="tca-col-md-12 tca-mb-3"
                    id="tca-section-local"
                  >
                    <label>Unidade</label>
                    <select
                      className="tca-form-control"
                      name="local_realizacao_atividades"
                      value={formData.local_realizacao_atividades}
                      onChange={(e) => {
                        const val = e.target.value;
                        console.log("[Filial] Unidade mudou para:", val);
                        handleChange(e);
                        // Ao trocar para FILIAL, auto-seleciona a primeira filial
                        // disponível para disparar o carregamento via GET /filiais/:id.
                        if (val === "FILIAL") {
                          if (
                            !localAtividade ||
                            localAtividade === "" ||
                            localAtividade === "MATRIZ"
                          ) {
                            const primeiraFilial = filiais.find(
                              (f) =>
                                f.id !== 0 &&
                                f.id !== undefined &&
                                f.id !== null,
                            );
                            if (primeiraFilial) {
                              const id = String(primeiraFilial.id);
                              console.log(
                                "[Filial] auto-selecionando primeira filial:",
                                id,
                              );
                              setLocalAtividade(id);
                            }
                          }
                        } else if (val === "MATRIZ") {
                          setLocalAtividade("MATRIZ");
                        }
                      }}
                    >
                      <option value="" disabled>
                        Selecione
                      </option>
                      <option value="MATRIZ">Matriz</option>
                      {mostrarFiliais && <option value="FILIAL">Filial</option>}
                    </select>
                  </div>

                  {mostrarFiliais &&
                    formData.local_realizacao_atividades === "FILIAL" && (
                      <>
                        <div id="tca-section-filial" />
                        <hr className="tca-hr" />
                        <div
                          className="tca-col-md-12 tca-mb-3"
                          id="filial-info"
                        >
                          <label>
                            <h3>Informações da Filial</h3>
                          </label>
                        </div>

                        <div className="tca-col-md-4 tca-mb-3">
                          <label>Nome da Filial</label>
                          <input
                            className="tca-form-control"
                            name="nome_filial"
                            value={formData.nome_filial}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="tca-col-md-4 tca-mb-3">
                          <label>CPF/CNPJ</label>
                          <input
                            type="text"
                            className="tca-form-control"
                            name="cpfCnpj"
                            value={formData.cpfCnpj}
                            onChange={handleChange}
                          />
                        </div>

                        <div
                          className="tca-col-md-4 tca-mb-3"
                          // style={{ width: "100%" }}
                        >
                          <label>CEP</label>
                          <input
                            type="text"
                            className="tca-form-control"
                            name="cepFilial"
                            value={formData.cepFilial || ""}
                            onChange={handleChange}
                            onBlur={handleCepFilialBlur}
                            maxLength={9}
                            placeholder="Apenas números"
                          />
                        </div>

                        <div
                          className="tca-col-md-4 tca-mb-3"
                          // style={{ width: "100%" }}
                        >
                          <label>Endereço</label>
                          <input
                            type="text"
                            className="tca-form-control"
                            name="enderecoFilial"
                            value={formData.enderecoFilial || ""}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="tca-col-md-4 tca-mb-3">
                          <label>Bairro/Distrito</label>
                          <input
                            type="text"
                            className="tca-form-control"
                            name="bairro"
                            value={formData.bairro}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="tca-col-md-4 tca-mb-3">
                          <label>Municipio</label>
                          <input
                            type="text"
                            className="tca-form-control"
                            name="municipio"
                            value={formData.municipio}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="tca-col-md-4 tca-mb-3">
                          <label>UF</label>
                          <input
                            type="text"
                            className="tca-form-control"
                            name="uf"
                            value={formData.uf}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="tca-col-md-4 tca-mb-3">
                          <label>Telefone 1</label>
                          <InputMask
                            mask="(99)99999-9999"
                            maskChar=" "
                            className="tca-form-control"
                            name="telefones"
                            value={formData.telefones}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="tca-col-md-4 tca-mb-3">
                          <label>Telefone 2</label>
                          <InputMask
                            mask="(99)99999-9999"
                            maskChar=" "
                            className="tca-form-control"
                            name="telefones2"
                            value={formData.telefones2}
                            onChange={handleChange}
                          />
                        </div>

                        <div
                          className="tca-col-md-4 tca-mb-3"
                          // style={{ width: "100%" }}
                        >
                          <label>E-mail Contrato</label>
                          <input
                            type="email"
                            className="tca-form-control"
                            name="emailContratos"
                            value={formData.emailContratos}
                            onChange={handleChange}
                          />
                        </div>

                        <div
                          className="tca-col-md-4 tca-mb-3"
                          // style={{ width: "100%" }}
                        >
                          <label>E-mail Financeiro</label>
                          <input
                            type="email"
                            className="tca-form-control"
                            name="emailFinanceiro"
                            value={formData.emailFinanceiro}
                            onChange={handleChange}
                          />
                        </div>

                        <div
                          className="tca-col-md-12 tca-mb-3"
                          id="filial-info"
                        >
                          <label>
                            <h3>Representante da Filial</h3>
                          </label>
                        </div>

                        <div className="tca-col-md-4 tca-mb-3">
                          <label>Representante Por</label>
                          <input
                            type="text"
                            className="tca-form-control"
                            name="representadaPor"
                            value={formData.representadaPor}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="tca-col-md-2 tca-mb-3">
                          <label>Cargo</label>
                          <input
                            type="text"
                            className="tca-form-control"
                            name="cargoRepresentante"
                            value={formData.cargoRepresentante}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="tca-col-md-4 tca-mb-3">
                          <label>E-mail</label>
                          <input
                            type="email"
                            className="tca-form-control"
                            name="emailRepresentante"
                            value={formData.emailRepresentante}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="tca-col-md-4 tca-mb-3">
                          <label>Responsvel/Supervisor do Estágio</label>
                          <input
                            type="text"
                            className="tca-form-control"
                            name="responsavelSupervisor"
                            value={formData.responsavelSupervisor}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="tca-col-md-2 tca-mb-3">
                          <label>Cargo</label>
                          <input
                            type="text"
                            className="tca-form-control"
                            name="cargoSupervisor"
                            value={formData.cargoSupervisor}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="tca-col-md-4 tca-mb-3">
                          <label>E-mail</label>
                          <input
                            type="email"
                            className="tca-form-control"
                            name="emailSupervisor"
                            value={formData.emailSupervisor}
                            onChange={handleChange}
                          />
                        </div>

                        {filialLoading && (
                          <div
                            className="tca-col-md-12 tca-mb-3"
                            style={{
                              color: "#475569",
                              fontStyle: "italic",
                              fontSize: "0.875rem",
                            }}
                          >
                            Carregando dados da filial…
                          </div>
                        )}
                      </>
                    )}
                </>
              )}
            </div>

            <hr className="tca-hr" />
            <h6 className="tca-h6">
              Representante Legal (quem assina o contrato)
            </h6>
            <div className="tca-row">
              <div className="tca-col-md-6 tca-mb-3">
                <label>Nome</label>
                <input
                  className="tca-form-control"
                  name="nome_representante_legal"
                  value={formData.nome_representante_legal}
                  onChange={handleChange}
                  maxLength={100}
                />
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>Cargo</label>
                <input
                  className="tca-form-control"
                  name="cargo_representante_legal"
                  value={formData.cargo_representante_legal}
                  onChange={handleChange}
                />
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>Telefone</label>
                <InputMask
                  mask="(99)99999-9999"
                  maskChar=" "
                  className="tca-form-control"
                  name="telefone_representante_legal"
                  value={formData.telefone_representante_legal}
                  onChange={handleChange}
                />
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>E-mail</label>
                <input
                  type="email"
                  className="tca-form-control"
                  name="email_representante_legal"
                  value={formData.email_representante_legal}
                  onChange={handleChange}
                />
              </div>
            </div>

            <hr className="tca-hr" />
            <h6 className="tca-h6">Dados da Aprendizagem</h6>
            <div className="tca-row">
              <div className="div-data-inicio-contrato">
                <label className="all-label" htmlFor="data_inicio_contrato">
                  Data do início do contrato:
                </label>
                <select
                  name="data_inicio_contrato"
                  id="data_inicio_contrato"
                  disabled={!permissao?.podeEditarEmpresa}
                  value={formData.data_inicio_contrato || ""}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    Selecione
                  </option>
                  {datasVigenciaFuturas.map((d) => (
                    <option key={d.id} value={d.valorObj.data}>
                      {new Date(
                        d.valorObj.data + "T00:00:00",
                      ).toLocaleDateString("pt-BR")}
                    </option>
                  ))}
                </select>
              </div>

              <div className="tca-col-md-4 tca-mb-3">
                <label>Salário</label>
                <NumericFormat
                  className="tca-form-control"
                  name="salario"
                  value={formData.salario}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  prefix="R$ "
                  onValueChange={(values) =>
                    setFormData((p) => ({
                      ...p,
                      salario: values.formattedValue,
                    }))
                  }
                />
              </div>

              <div className="tca-col-md-4 tca-mb-3">
                <label>Carga Horária Diária</label>
                <input
                  className="tca-form-control"
                  name="carga_horaria_diaria"
                  value={formData.carga_horaria_diaria}
                  onChange={handleChange}
                />
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>Curso</label>
                <input
                  className="tca-form-control"
                  name="curso"
                  value={formData.curso}
                  onChange={handleChange}
                />
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>Tutor(a) na Empresa</label>
                <input
                  className="tca-form-control"
                  name="tutor_aprendiz_empresa"
                  value={formData.tutor_aprendiz_empresa}
                  onChange={handleChange}
                />
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>Cargo do(a) Tutor(a)</label>
                <input
                  className="tca-form-control"
                  name="cargo_tutor"
                  value={formData.cargo_tutor}
                  onChange={handleChange}
                />
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>Nome do(a) Tutor(a) p/ Atividades Práticas</label>
                <input
                  className="tca-form-control"
                  name="nome_tutor"
                  value={formData.nome_tutor}
                  onChange={handleChange}
                />
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>E-mail do(a) Tutor(a)</label>
                <input
                  type="email"
                  className="tca-form-control"
                  name="email_tutor"
                  value={formData.email_tutor}
                  onChange={handleChange}
                />
              </div>

              <div className="tca-col-md-12 tca-mb-3">
                <label>Endereço Completo da Aprendizagem Prática</label>
                <textarea
                  className="tca-form-control"
                  rows="2"
                  name="endereco_aprendizagem_pratica"
                  value={formData.endereco_aprendizagem_pratica}
                  onChange={handleChange}
                ></textarea>
              </div>

              <div className="tca-col-md-12 tca-mb-3">
                <label>Endereço da Aprendizagem Teórica</label>
                <input
                  type="text"
                  name="endereco_aprendizagem_teorica"
                  className="tca-form-control bg-white-important"
                  readOnly={
                    ![2, 3, 4].includes(Number(user?.acesso_autorizacao))
                  }
                  onChange={handleChange}
                  value={formData.endereco_aprendizagem_teorica || ""}
                />
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>Horário Aprendizagem Prática</label>
                <div className="tca-d-flex tca-gap-2">
                  <input
                    type="time"
                    className="tca-form-control"
                    name="horario_pratica_inicio"
                    value={formData.horario_pratica_inicio}
                    onChange={handleChange}
                  />
                  <input
                    type="time"
                    className="tca-form-control"
                    name="horario_pratica_fim"
                    value={formData.horario_pratica_fim}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>Horário Aprendizagem Teórica</label>
                <select
                  className="tca-form-control"
                  name="horario_teorico_id"
                  value={formData.horario_teorico_id || ""}
                  onChange={(e) => {
                    const id = e.target.value;
                    const horario = horariosTeoricos.find(
                      (h) => String(h.id) === String(id),
                    );
                    setFormData({
                      ...formData,
                      horario_teorico_id: id,
                      horario_teorica_inicio: horario?.horario_inicio || "",
                      horario_teorica_fim: horario?.horario_fim || "",
                    });
                  }}
                >
                  <option value="">Selecione</option>
                  {horariosTeoricosDisponiveis.map((h) => (
                    <option key={h.id} value={h.id}>
                      {formatHorarioTeoricoLabel(h)}
                    </option>
                  ))}
                </select>
                {!cidadeAtualEmpregador && (
                  <small
                    style={{
                      display: "block",
                      marginTop: "4px",
                      color: "#666",
                    }}
                  >
                    Selecione uma cidade acima para listar os horários
                    disponíveis.
                  </small>
                )}
                {cidadeAtualEmpregador &&
                  horariosTeoricosDisponiveis.length === 0 && (
                    <small
                      style={{
                        display: "block",
                        marginTop: "4px",
                        color: "#b45309",
                      }}
                    >
                      Nenhum horário cadastrado para esta cidade.
                    </small>
                  )}
              </div>
            </div>
          </div>

          {/* O campo "Unidade *" redundante foi removido daqui para evitar duplicação. */}
        </section>

        <section className="tca-card tca-mb-4" id="tca-section-dados-aprendiz">
          <div className="tca-card-header" style={{ position: "relative" }}>
            Dados do(a) Aprendiz
            {idadeCalculada !== null && (
              <span
                style={{
                  position: "absolute",
                  right: "16px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "0.80rem",
                  padding: "4px 10px",
                  borderRadius: "12px",
                  backgroundColor: idadeCalculada >= 18 ? "#dcfce7" : "#fef08a",
                  color: idadeCalculada >= 18 ? "#166534" : "#854d0e",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                }}
              >
                {idadeCalculada >= 18 ? "Maior de Idade" : "Menor de Idade"}
              </span>
            )}
          </div>
          <div className="tca-card-body">
            {estudanteNaoEncontrado && (
              <div
                style={{
                  backgroundColor: "#fef2f2",
                  border: "1px solid #f87171",
                  borderLeft: "4px solid #ef4444",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  color: "#991b1b",
                  padding: "1rem 1.25rem",
                  borderRadius: "8px",
                  marginBottom: "1.5rem",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  lineHeight: "1.5",
                }}
              >
                <AlertCircle
                  size={22}
                  style={{ flexShrink: 0, marginTop: "2px", color: "#ef4444" }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <span style={{ fontWeight: "700", fontSize: "0.95rem" }}>
                    Aprendiz não encontrado
                  </span>
                  <span style={{ fontSize: "0.9rem", color: "#7f1d1d" }}>
                    O contrato não poderá ser validado enquanto o cadastro do(a)
                    aprendiz não for realizado no sistema.
                  </span>
                </div>
              </div>
            )}

            <div className="tca-row">
              <div className="tca-col-md-6 tca-mb-3">
                <label>CPF do Aprendiz:</label>
                <input
                  type="text"
                  className="tca-form-control"
                  name="cpf"
                  maxLength={14}
                  onChange={handleChange}
                  value={formData.cpf || ""}
                />
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>Nome Completo:</label>
                <input
                  type="text"
                  className="tca-form-control"
                  name="nome_completo"
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.nome_completo || ""}
                />
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>Telefone/Informações adicionais de contato:</label>
                <InputMask
                  mask="(99)99999-9999"
                  maskChar=" "
                  type="text"
                  className="tca-form-control"
                  name="telefone_01"
                  autoComplete="off"
                  onChange={handleChange}
                  value={formData.telefone_01 || ""}
                />
              </div>

              <div className="tca-col-md-6 tca-mb-3">
                <label>E-mail:</label>
                <input
                  type="email"
                  className="tca-form-control"
                  name="email_pessoal"
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.email_pessoal || ""}
                />
              </div>
            </div>

            {estudanteNaoEncontrado && (
              <div
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  padding: "1.25rem 1.5rem",
                  marginBottom: "1.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "24px",
                  boxShadow:
                    "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    flex: "1 1 300px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "700",
                      color: "#1e293b",
                      fontSize: "0.95rem",
                    }}
                  >
                    Precisa de ajuda com o cadastro?
                  </span>
                  <span
                    style={{
                      fontSize: "0.85rem",
                      color: "#475569",
                      lineHeight: "1.5",
                    }}
                  >
                    Em caso de dúvida entre em contato com nossa empresa
                    através.
                    <br />
                    Telefone/Whatsapp: <strong>(31) 3428100</strong> ; E-mail:{" "}
                    <a
                      href="mailto:atendimento@cieemg.org.br"
                      style={{
                        color: "#185fa5",
                        textDecoration: "none",
                        fontWeight: "600",
                      }}
                    >
                      atendimento@cieemg.org.br
                    </a>
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setCienteCadastro(true)}
                    disabled={cienteCadastro}
                    style={{
                      backgroundColor: cienteCadastro ? "#dcfce7" : "#f1f5f9",
                      color: cienteCadastro ? "#166534" : "#475569",
                      border: cienteCadastro
                        ? "1px solid #bbf7d0"
                        : "1px solid #cbd5e1",
                      borderRadius: "8px",
                      padding: "0.75rem 1.25rem",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                      cursor: cienteCadastro ? "default" : "pointer",
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {cienteCadastro ? (
                      <>
                        <Check size={18} />
                        Ciente
                      </>
                    ) : (
                      "Estou Ciente da Situação"
                    )}
                  </button>

                  <button
                    type="button"
                    disabled={!cienteCadastro}
                    onClick={() =>
                      window.open(
                        "https://appcadastro.cieemg.org.br/",
                        "_blank",
                      )
                    }
                    style={{
                      backgroundColor: cienteCadastro ? "#185fa5" : "#94a3b8",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "8px",
                      padding: "0.75rem 1.75rem",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                      cursor: cienteCadastro ? "pointer" : "not-allowed",
                      boxShadow: cienteCadastro
                        ? "0 4px 6px rgba(24, 95, 165, 0.2)"
                        : "none",
                      transition: "all 0.2s ease",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      opacity: cienteCadastro ? 1 : 0.7,
                    }}
                    onMouseOver={(e) => {
                      if (!cienteCadastro) return;
                      e.target.style.backgroundColor = "#10477e";
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 6px 8px rgba(24, 95, 165, 0.3)";
                    }}
                    onMouseOut={(e) => {
                      if (!cienteCadastro) return;
                      e.target.style.backgroundColor = "#185fa5";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 4px 6px rgba(24, 95, 165, 0.2)";
                    }}
                  >
                    Fazer Cadastro do Aprendiz
                  </button>
                </div>
              </div>
            )}

            <div
              style={{
                backgroundColor: "#1e528a",
                color: "#fff",
                padding: "1rem",
                borderRadius: "8px",
                marginTop: "1rem",
              }}
            >
              <span style={{ fontWeight: "bold" }}>Observações:</span>
              <br />
              1) É de suma importância a inscrição do jovem em nosso site com
              antecedência.
              <br />
              2) Observar se o horário escolar é contrário ao horário de
              aprendizagem.
              <br />
              3) O início da aprendizagem só pode ocorrer após assinatura do
              contrato.
            </div>
          </div>
        </section>

        <section className="tca-card tca-mb-4" id="tca-section-aprendiz">
          <div className="tca-card-header">
            2. Identificação Pessoal do Aprendiz
          </div>
          <div className="tca-card-body">
            <div className="tca-row">
              <div className="tca-col-md-4 tca-mb-3">
                <label>Nome Social</label>
                <input
                  className="tca-form-control"
                  name="nome_social"
                  value={formData.nome_social}
                  onChange={handleChange}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Estado Natal</label>
                <input
                  className="tca-form-control"
                  name="estado_natal"
                  value={formData.estado_natal}
                  onChange={handleChange}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Naturalidade</label>
                <input
                  className="tca-form-control"
                  name="naturalidade"
                  value={formData.naturalidade}
                  onChange={handleChange}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Nacionalidade</label>
                <input
                  className="tca-form-control"
                  name="nacionalidade"
                  value={formData.nacionalidade}
                  onChange={handleChange}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Data de Nascimento</label>
                <input
                  type="date"
                  className="tca-form-control"
                  name="data_nascimento"
                  value={formData.data_nascimento}
                  onChange={handleChange}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Sexo</label>
                <select
                  className="tca-form-control"
                  name="sexo"
                  value={formData.sexo}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecione</option>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                </select>
              </div>
              {idadeCalculada !== null && idadeCalculada < 18 && (
                <section className="tca-card tca-mb-4">
                  <div className="tca-card-header">
                    Responsável Legal do Aprendiz
                    <small
                      style={{
                        marginLeft: "10px",
                        color: "#fff",
                        fontWeight: "normal",
                      }}
                    >
                      (Aprendiz menor de idade — {idadeCalculada} anos)
                    </small>
                  </div>
                  <div className="tca-card-body">
                    <div className="tca-row">
                      <div className="tca-col-md-6 tca-mb-3">
                        <label>Nome</label>
                        <input
                          className="tca-form-control"
                          name="nome_aprendiz_menor_idade"
                          value={formData.nome_aprendiz_menor_idade || ""}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="tca-col-md-3 tca-mb-3">
                        <label>Telefone</label>
                        <InputMask
                          mask="(99)99999-9999"
                          maskChar=" "
                          className="tca-form-control"
                          name="telefone_aprendiz_menor_idade"
                          value={formData.telefone_aprendiz_menor_idade || ""}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="tca-col-md-3 tca-mb-3">
                        <label>CPF</label>
                        <InputMask
                          mask="999.999.999-99"
                          maskChar=" "
                          className="tca-form-control"
                          name="cpf_aprendiz_menor_idade"
                          value={formData.cpf_aprendiz_menor_idade || ""}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="tca-col-md-6 tca-mb-3">
                        <label>E-mail</label>
                        <input
                          type="email"
                          className="tca-form-control"
                          name="email_aprendiz_menor_idade"
                          value={formData.email_aprendiz_menor_idade || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                </section>
              )}
              <div className="tca-col-md-4 tca-mb-3">
                <label>Tipo Sanguíneo</label>
                <select
                  className="tca-form-control"
                  name="tipo_sanguineo"
                  value={formData.tipo_sanguineo}
                  onChange={handleChange}
                >
                  <option value="">Selecione</option>
                  <option>A</option>
                  <option>B</option>
                  <option>AB</option>
                  <option>O</option>
                </select>
              </div>

              <div className="tca-col-md-4 tca-mb-3">
                <label>Cor</label>
                <input
                  className="tca-form-control"
                  name="cor"
                  value={formData.cor}
                  onChange={handleChange}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Fumante?</label>
                <select
                  className="tca-form-control"
                  name="fumante"
                  value={formData.fumante}
                  onChange={handleChange}
                >
                  <option value={false}>Não</option>
                  <option value={true}>Sim</option>
                </select>
              </div>

              <div className="tca-col-md-4 tca-mb-3">
                <label>Estado Civil</label>
                <select
                  className="tca-form-control"
                  name="estado_civil"
                  value={formData.estado_civil}
                  onChange={handleChange}
                >
                  <option value="">Selecione</option>
                  <option>Solteiro(a)</option>
                  <option>Casado(a)</option>
                  <option>Divorciado(a)</option>
                  <option>Viúvo(a)</option>
                  <option>União Estável</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        <section className="tca-card tca-mb-4" id="tca-section-mae">
          <div className="tca-card-header">3. Dados da Mãe</div>
          <div className="tca-card-body">
            <div className="tca-row">
              <div className="tca-col-md-8 tca-mb-3">
                <label>Nome</label>
                <input
                  className="tca-form-control"
                  name="nome_mae"
                  value={formData.nome_mae}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("nome_mae")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>CPF</label>
                <InputMask
                  mask="999.999.999-99"
                  maskChar=" "
                  className="tca-form-control"
                  name="cpf_mae"
                  value={formData.cpf_mae}
                  onChange={handleChange}
                  disabled={isCampoBloqueadoParaEmpresa("cpf_mae")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Telefone</label>
                <InputMask
                  mask="(99)99999-9999"
                  maskChar=" "
                  className="tca-form-control"
                  name="telefone_mae"
                  value={formData.telefone_mae}
                  onChange={handleChange}
                  disabled={isCampoBloqueadoParaEmpresa("telefone_mae")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>E-mail</label>
                <input
                  type="email"
                  className="tca-form-control"
                  name="email_mae"
                  value={formData.email_mae}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("email_mae")}
                />
              </div>
              <div className="tca-col-md-4 tcfa-mb-3">
                <label>Data de Nascimento</label>
                <input
                  type="date"
                  className="tca-form-control"
                  name="data_nascimento_mae"
                  value={formData.data_nascimento_mae}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("data_nascimento_mae")}
                />
              </div>
              <div className="tca-col-md-6 tca-mb-3">
                <label>Naturalidade</label>
                <input
                  className="tca-form-control"
                  name="naturalidade_mae"
                  value={formData.naturalidade_mae}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("naturalidade_mae")}
                />
              </div>
              <div className="tca-col-md-6 tca-mb-3">
                <label>Estado Civil</label>
                <input
                  className="tca-form-control"
                  name="estado_civil_mae"
                  value={formData.estado_civil_mae}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("estado_civil_mae")}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="tca-card tca-mb-4" id="tca-section-endereco-aprendiz">
          <div className="tca-card-header">
            4. Endereço Principal do Aprendiz
          </div>
          <div className="tca-card-body">
            <div className="tca-row">
              <div className="tca-col-md-8 tca-mb-3">
                <label>Logradouro</label>
                <input
                  className="tca-form-control"
                  name="logradouro"
                  value={formData.logradouro}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("logradouro")}
                />
              </div>
              <div className="tca-col-md-2 tca-mb-3">
                <label>Nº</label>
                <input
                  className="tca-form-control"
                  name="numero_endereco"
                  value={formData.numero_endereco}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("numero_endereco")}
                />
              </div>
              <div className="tca-col-md-2 tca-mb-3">
                <label>Compl.</label>
                <input
                  className="tca-form-control"
                  name="complemento"
                  value={formData.complemento}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("complemento")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Bairro</label>
                <input
                  className="tca-form-control"
                  name="bairro"
                  value={formData.bairro}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("bairro")}
                />
              </div>
              <div className="tca-col-md-3 tca-mb-3">
                <label>CEP</label>
                <InputMask
                  mask="99999-999"
                  maskChar=" "
                  className="tca-form-control"
                  name="cep"
                  value={formData.cep}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("cep")}
                />
              </div>
              <div className="tca-col-md-3 tca-mb-3">
                <label>Cidade</label>
                <input
                  className="tca-form-control"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("cidade")}
                />
              </div>
              <div className="tca-col-md-2 tca-mb-3">
                <label>Estado</label>
                <input
                  className="tca-form-control"
                  name="estado_endereco"
                  value={formData.estado_endereco}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("estado_endereco")}
                />
              </div>
              <div className="tca-col-md-3 tca-mb-3">
                <label>País</label>
                <input
                  className="tca-form-control"
                  name="pais"
                  value={formData.pais}
                  onChange={handleChange}
                  placeholder="Brasil"
                  readOnly={isCampoBloqueadoParaEmpresa("pais")}
                />
              </div>
              <div className="tca-col-md-3 tca-mb-3">
                <label>Telefone 02</label>
                <InputMask
                  mask="(99)99999-9999"
                  maskChar=" "
                  className="tca-form-control"
                  name="telefone_02"
                  value={formData.telefone_02}
                  onChange={handleChange}
                  disabled={isCampoBloqueadoParaEmpresa("telefone_02")}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="tca-card tca-mb-4">
          <div className="tca-card-header">5. Escolaridade</div>
          <div className="tca-card-body">
            <div className="tca-row">
              <div className="tca-col-md-4 tca-mb-3">
                <label>Grau de Instrução</label>
                <input
                  className="tca-form-control"
                  name="grau_instrucao"
                  value={formData.grau_instrucao}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("grau_instrucao")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Curso</label>
                <input
                  className="tca-form-control"
                  name="curso_escolaridade"
                  value={formData.curso_escolaridade}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("curso_escolaridade")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Instituição</label>
                <input
                  className="tca-form-control"
                  name="instituicao_ensino"
                  value={formData.instituicao_ensino}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("instituicao_ensino")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Conclusão (ou previsão)</label>
                <input
                  className="tca-form-control"
                  name="conclusao"
                  value={formData.conclusao}
                  onChange={handleChange}
                  placeholder="Ex: 12/2026"
                  readOnly={isCampoBloqueadoParaEmpresa("conclusao")}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="tca-card tca-mb-4">
          <div className="tca-card-header">
            6. Documentos
          </div>
          <div className="tca-card-body">
            <div className="tca-row">
              <div className="tca-col-md-4 tca-mb-3">
                <label>PIS</label>
                <InputMask
                  mask="999.99999.99-9"
                  maskChar=" "
                  className="tca-form-control"
                  name="pis"
                  value={formData.pis}
                  onChange={handleChange}
                  disabled={isCampoBloqueadoParaEmpresa("pis")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>RG</label>
                <InputMask
                  mask="99.999.999-9"
                  maskChar=" "
                  className="tca-form-control"
                  name="rg"
                  value={formData.rg}
                  onChange={handleChange}
                  disabled={isCampoBloqueadoParaEmpresa("rg")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Data Emissão RG</label>
                <input
                  type="date"
                  className="tca-form-control"
                  name="data_emissao_rg"
                  value={formData.data_emissao_rg}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("data_emissao_rg")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Órgão Emissor RG</label>
                <input
                  className="tca-form-control"
                  name="orgao_emissor_rg"
                  value={formData.orgao_emissor_rg}
                  onChange={handleChange}
                  placeholder="SSP/MG"
                  readOnly={isCampoBloqueadoParaEmpresa("orgao_emissor_rg")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Estado RG</label>
                <input
                  className="tca-form-control"
                  name="estado_rg"
                  value={formData.estado_rg}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("estado_rg")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Título de Eleitor</label>
                <input
                  className="tca-form-control"
                  name="titulo_eleitor"
                  value={formData.titulo_eleitor}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("titulo_eleitor")}
                />
              </div>
              <div className="tca-col-md-2 tca-mb-3">
                <label>Zona</label>
                <input
                  className="tca-form-control"
                  name="zona"
                  value={formData.zona}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("zona")}
                />
              </div>
              <div className="tca-col-md-2 tca-mb-3">
                <label>Seção</label>
                <input
                  className="tca-form-control"
                  name="secao"
                  value={formData.secao}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("secao")}
                />
              </div>
              <div className="tca-col-md-4 tca-mb-3">
                <label>Data Emissão Título</label>
                <input
                  type="date"
                  className="tca-form-control"
                  name="data_emissao_titulo"
                  value={formData.data_emissao_titulo}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("data_emissao_titulo")}
                />
              </div>
            </div>
          </div>
        </section>

        {formData.sexo === "M" &&
          idadeCalculada !== null &&
          idadeCalculada > 18 && (
            <section className="tca-card tca-mb-4">
              <div className="tca-card-header">
                7. Situação Militar{" "}
                <small className="tca-text-muted">(homens &gt; 18 anos)</small>
              </div>
              <div className="tca-card-body">
                <div className="tca-row">
                  <div className="tca-col-md-6 tca-mb-3">
                    <label>Nº Certificado de Reservista</label>
                    <input
                      className="tca-form-control"
                      name="numero_certificado_reservista"
                      value={formData.numero_certificado_reservista}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="tca-col-md-3 tca-mb-3">
                    <label>Data Emissão</label>
                    <input
                      type="date"
                      className="tca-form-control"
                      name="data_emissao_certificado"
                      value={formData.data_emissao_certificado}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="tca-col-md-3 tca-mb-3">
                    <label>Órgão Expedição</label>
                    <input
                      className="tca-form-control"
                      name="orgao_expedicao"
                      value={formData.orgao_expedicao}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="tca-col-md-12 tca-mb-3">
                    <label>Situação Militar</label>
                    <input
                      className="tca-form-control"
                      name="situacao_militar"
                      value={formData.situacao_militar}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </section>
          )}

        <section className="tca-card tca-mb-4" id="tca-section-bancario">
          <div className="tca-card-header">
            8. Conta Bancária (Bradesco 237)
          </div>
          <div className="tca-card-body">
            <div className="tca-row">
              <div className="tca-col-md-4 tca-mb-3">
                <label>Banco</label>
                <input
                  className="tca-form-control"
                  name="banco_pagamento"
                  value={formData.banco_pagamento}
                  onChange={handleChange}
                  readOnly={isCampoBloqueadoParaEmpresa("banco_pagamento")}
                />
              </div>
              <div className="tca-col-md-3 tca-mb-3">
                <label>Agência</label>{" "}
                <input
                  className="tca-form-control"
                  name="agencia"
                  value={formData.agencia}
                  onChange={handleChange}
                  maxLength={6}
                  inputMode="numeric"
                  readOnly={isCampoBloqueadoParaEmpresa("agencia")}
                />
              </div>
              <div className="tca-col-md-1 tca-mb-3">
                <label>DIV</label>{" "}
                <input
                  className="tca-form-control"
                  name="div1"
                  value={formData.div1}
                  onChange={handleChange}
                  maxLength={1}
                  inputMode="numeric"
                  readOnly={isCampoBloqueadoParaEmpresa("div1")}
                />
              </div>
              <div className="tca-col-md-3 tca-mb-3">
                <label>Conta</label>{" "}
                <input
                  className="tca-form-control"
                  name="conta"
                  value={formData.conta}
                  onChange={handleChange}
                  maxLength={12}
                  inputMode="numeric"
                  readOnly={isCampoBloqueadoParaEmpresa("conta")}
                />
              </div>
              <div className="tca-col-md-1 tca-mb-3">
                <label>DIV</label>{" "}
                <input
                  className="tca-form-control"
                  name="div2"
                  value={formData.div2}
                  onChange={handleChange}
                  maxLength={1}
                  inputMode="numeric"
                  readOnly={isCampoBloqueadoParaEmpresa("div2")}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="tca-card tca-mb-4">
          <div className="tca-card-header">
            9. Ambiente de Trabalho
          </div>
          <div className="tca-card-body">
            <div className="tca-row">
              <div className="tca-col-md-4 tca-mb-3">
                <label>Trabalhou em ambiente insalubre?</label>
                <select
                  className="tca-form-control"
                  name="trabalhou_ambiente_insalubre"
                  value={String(formData.trabalhou_ambiente_insalubre)}
                  disabled={isCampoBloqueadoParaEmpresa(
                    "trabalhou_ambiente_insalubre",
                  )}
                  onChange={(e) => {
                    if (
                      isCampoBloqueadoParaEmpresa("trabalhou_ambiente_insalubre")
                    ) {
                      toastWarning(
                        "Este campo é de responsabilidade do estudante.",
                      );
                      return;
                    }
                    const novoValor = e.target.value === "true";
                    setFormData((prev) => {
                      const novoState = {
                        ...prev,
                        trabalhou_ambiente_insalubre: novoValor,
                      };
                      if (!novoValor) {
                        novoState.risco_exposto = "";
                      }
                      return novoState;
                    });
                  }}
                >
                  <option value="false">Não</option>
                  <option value="true">Sim</option>
                </select>
              </div>
              {formData.trabalhou_ambiente_insalubre === true && (
                <div className="tca-col-md-8 tca-mb-3">
                  <label>A qual risco esteve exposto?</label>
                  <textarea
                    className="tca-form-control"
                    rows="2"
                    name="risco_exposto"
                    value={formData.risco_exposto}
                    readOnly={isCampoBloqueadoParaEmpresa("risco_exposto")}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        risco_exposto: e.target.value,
                      }))
                    }
                  ></textarea>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="tca-card tca-mb-4" id="tca-section-vt">
          <div className="tca-card-header">
            10. Vale-Transporte
          </div>
          <div className="tca-card-body">
            <div className="tca-mb-3">
              <label>I) Endereço residencial</label>
              <textarea
                className="tca-form-control"
                rows="2"
                name="endereco_residencial_vt"
                value={formData.endereco_residencial_vt}
                onChange={handleChange}
                readOnly={isCampoBloqueadoParaEmpresa("endereco_residencial_vt")}
              ></textarea>
            </div>
            <div className="tca-mb-3">
              <label>II) Serviços e meios de transporte</label>
              <textarea
                className="tca-form-control"
                rows="2"
                name="servicos_meios_transporte"
                value={formData.servicos_meios_transporte}
                onChange={handleChange}
                readOnly={isCampoBloqueadoParaEmpresa("servicos_meios_transporte")}
              ></textarea>
            </div>
            <div className="tca-row">
              <div className="tca-col-md-12 tca-mb-3">
                <h6 className="tca-h6">Prática</h6>
                <div className="tca-row">
                  <div className="tca-col-3">
                    <label>Qtd/dia</label>
                    <input
                      className="tca-form-control"
                      name="qtd_dia_pratica"
                      value={formData.qtd_dia_pratica}
                      onChange={handleChange}
                      readOnly={isCampoBloqueadoParaEmpresa("qtd_dia_pratica")}
                    />
                  </div>
                  <div className="tca-col-5">
                    <label>Linha</label>
                    <input
                      className="tca-form-control"
                      name="linha_pratica"
                      value={formData.linha_pratica}
                      onChange={handleChange}
                      readOnly={isCampoBloqueadoParaEmpresa("linha_pratica")}
                    />
                  </div>
                  <div className="tca-col-4">
                    <label>Tarifa</label>
                    <input
                      className="tca-form-control"
                      name="tarifa_pratica"
                      value={formData.tarifa_pratica}
                      onChange={handleChange}
                      readOnly={isCampoBloqueadoParaEmpresa("tarifa_pratica")}
                    />
                  </div>
                </div>
              </div>
              <div className="tca-col-md-12 tca-mb-3">
                <h6 className="tca-h6">Teórica</h6>
                <div className="tca-row">
                  <div className="tca-col-3">
                    <label>Qtd/dia</label>
                    <input
                      className="tca-form-control"
                      name="qtd_dia_teorica"
                      value={formData.qtd_dia_teorica}
                      onChange={handleChange}
                      readOnly={isCampoBloqueadoParaEmpresa("qtd_dia_teorica")}
                    />
                  </div>
                  <div className="tca-col-5">
                    <label>Linha</label>
                    <input
                      className="tca-form-control"
                      name="linha_teorica"
                      value={formData.linha_teorica}
                      onChange={handleChange}
                      readOnly={isCampoBloqueadoParaEmpresa("linha_teorica")}
                    />
                  </div>
                  <div className="tca-col-4">
                    <label>Tarifa</label>
                    <input
                      className="tca-form-control"
                      name="tarifa_teorica"
                      value={formData.tarifa_teorica}
                      onChange={handleChange}
                      readOnly={isCampoBloqueadoParaEmpresa("tarifa_teorica")}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="tca-form-check">
              <input
                type="checkbox"
                className="tca-form-check-input"
                id="deseja_vt"
                name="deseja_vale_transporte"
                checked={formData.deseja_vale_transporte}
                disabled={isCampoBloqueadoParaEmpresa("deseja_vale_transporte")}
                onChange={(e) => {
                  if (isCampoBloqueadoParaEmpresa("deseja_vale_transporte")) {
                    toastWarning(
                      "Este campo é de responsabilidade do estudante.",
                    );
                    return;
                  }
                  handleChange(e);
                }}
              />
              <label className="tca-form-check-label" htmlFor="deseja_vt">
                Desejo adquirir Vale-Transporte
              </label>
            </div>
          </div>
        </section>

        <section className="tca-card tca-mb-4" id="tca-section-dependentes">
          <div className="tca-card-header">
            11. Declaração de Encargos da Família (Imposto de Renda)
          </div>

          <div className="tca-card-body">
            <div className="tca-row">
              <div className="tca-col-md-6 tca-mb-3">
                <label>Possui dependentes?</label>
                <select
                  className="tca-form-control"
                  name="possui_dependentes"
                  value={formData.possui_dependentes}
                  onChange={handleChange}
                  disabled={isCampoBloqueadoParaEmpresa("possui_dependentes")}
                >
                  <option value="">Selecione</option>
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </select>
              </div>
              <div className="tca-col-md-6 tca-mb-3">
                <label>Nome do Declarante</label>
                <input
                  className="tca-form-control"
                  name="nome_declarante_ir"
                  value={formData.nome_declarante_ir}
                  onChange={handleChange}
                  disabled={
                    formData.possui_dependentes === "nao" ||
                    isCampoBloqueadoParaEmpresa("nome_declarante_ir")
                  }
                />
              </div>
              <div className="tca-col-md-3 tca-mb-3">
                <label>CPF</label>{" "}
                <InputMask
                  mask="999.999.999-99"
                  maskChar=" "
                  className="tca-form-control"
                  name="cpf_declarante_ir"
                  value={formData.cpf_declarante_ir}
                  onChange={handleChange}
                  disabled={
                    formData.possui_dependentes === "nao" ||
                    isCampoBloqueadoParaEmpresa("cpf_declarante_ir")
                  }
                />
              </div>
              <div className="tca-col-md-3 tca-mb-3">
                <label>Estado Civil</label>
                <input
                  className="tca-form-control"
                  name="estado_civil_ir"
                  value={formData.estado_civil_ir}
                  onChange={handleChange}
                  disabled={
                    formData.possui_dependentes === "nao" ||
                    isCampoBloqueadoParaEmpresa("estado_civil_ir")
                  }
                />
              </div>
            </div>
            {formData.possui_dependentes !== "nao" && (
              <>
                <hr className="tca-hr" />

                <h6 className="tca-h6">Dependentes</h6>

                {dependentesList.map((numero) => (
                  <div key={numero}>
                    {renderDependente(numero, () => removerDependente(numero))}
                  </div>
                ))}

                <div
                  style={{ display: "flex", gap: "10px", marginTop: "1rem" }}
                >
                  <button
                    type="button"
                    className="tca-btn tca-btn-outline-primary"
                    onClick={
                      isCampoBloqueadoParaEmpresa("dependente_1_nome")
                        ? undefined
                        : adicionarDependente
                    }
                    disabled={isCampoBloqueadoParaEmpresa("dependente_1_nome")}
                  >
                    + Adicionar dependente
                  </button>
                  <button
                    type="button"
                    className="tca-btn tca-btn-primary"
                    onClick={() => setMostrarDeclaracaoDependentes(true)}
                    disabled={isCampoBloqueadoParaEmpresa("dependente_1_nome")}
                  >
                    Salvar Dependentes
                  </button>
                </div>
              </>
            )}
          </div>
        </section>

        {mostrarDeclaracaoDependentes && (
          <div
            className="tca-modal-overlay"
            style={{
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.5)",
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget)
                setMostrarDeclaracaoDependentes(false);
            }}
          >
            <div
              className="tca-modal-content"
              style={{
                maxWidth: "650px",
                padding: "24px",
                borderRadius: "12px",
                background: "#fff",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              }}
            >
              <h3
                style={{
                  marginTop: 0,
                  color: "#1e3a8a",
                  fontSize: "1.25rem",
                  fontWeight: "bold",
                  borderBottom: "1px solid #e2e8f0",
                  paddingBottom: "12px",
                  marginBottom: "16px",
                }}
              >
                Declaração
              </h3>
              <div
                style={{
                  fontSize: "0.95rem",
                  lineHeight: "1.6",
                  color: "#334155",
                  textAlign: "justify",
                }}
              >
                <p>
                  Declaro, sob penas da lei, que as informações aqui prestadas
                  são verdadeiras e de minha inteira responsabilidade, não
                  cabendo a V.Sas. (fonte pagadora) qualquer responsabilidade a
                  fiscalização.
                </p>
                <p>
                  Sempre que ocorrer alteração nesta declaração, a mesma deverá
                  ser renovada.
                </p>
                <p
                  style={{
                    fontSize: "0.85rem",
                    backgroundColor: "#f1f5f9",
                    padding: "12px",
                    borderRadius: "8px",
                    borderLeft: "4px solid #cbd5e1",
                  }}
                >
                  <strong>Obs.:</strong> São dependentes para fins do imposto de
                  renda: cônjuge ou companheiro com quem o contribuinte tenha
                  filho ou viva há mais de cinco anos. Filhos ou enteados de até
                  21 anos ou em qualquer idade quando incapacitado física ou
                  mentalmente para o trabalho ou de até 24 anos se ainda estiver
                  cursando ensino superior ou escola técnica de segundo grau.
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "24px",
                }}
              >
                <button
                  type="button"
                  className="tca-btn tca-btn-primary"
                  onClick={() => {
                    setMostrarDeclaracaoDependentes(false);
                    setCienteDeclaracaoDependentes(true);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 24px",
                    fontSize: "1rem",
                    fontWeight: "bold",
                  }}
                >
                  <CheckCircle2 size={18} /> Ciente
                </button>
              </div>
            </div>
          </div>
        )}

        <section className="tca-card tca-mb-4">
          <div className="tca-card-header">
            12. Documentos para Admissão
          </div>
          <div className="tca-card-body">
            <div
              className="tca-anexos-container"
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
                gap: 14,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {[
                ["doc_comprovante_endereco", "Comprovante de Endereço"],
                [
                  "doc_declaracao_escolar",
                  "Declaração escolar / histórico Ensino Médio",
                ],
                [
                  "doc_identificacao_documento_indetificacao",
                  "Documento de Identificação",
                ],

                ["doc_pis", "Nº do PIS"],
                ["doc_tipo_sanguineo", "Comprovante tipo sanguíneo"],
                ["doc_certificado_reservista", "Certificado de Reservista"],
                ["doc_certidao_casamento", "Certidão de Casamento"],
                ["doc_titulo_eleitor", "Título de Eleitor"],
                ["doc_curriculum", "Currículo atualizado"],
                ["doc_curso_qualificacao", "Curso(s) de qualificação"],
                ["doc_foto_3x4", "Foto 3x4 recente"],
              ].map(([name, label]) => {
                const file = anexosFiles[name];
                const progress = anexosProgress[name] || 0;
                const status = anexosStatus[name];

                const isAttached = Boolean(file);
                const showProgress = status === "uploading" && !isAttached;
                const visualStatus =
                  status === "done" || isAttached ? "anexado" : "pendente";

                const statusConfig = {
                  anexado: {
                    icon: <CheckCircle2 size={16} color="#0f6e56" />,
                    label: file?.name || "Anexado",
                    color: "#0f6e56",
                    bg: "rgba(15, 110, 86, 0.08)",
                    border: "rgba(15, 110, 86, 0.35)",
                  },
                  pendente: {
                    icon: <AlertCircle size={16} color="#b45309" />,
                    label: "Pendente",
                    color: "#7c2d12",
                    bg: "rgba(180, 83, 9, 0.08)",
                    border: "rgba(180, 180, 175, 0.6)",
                  },
                }[visualStatus];

                const IconComponent =
                  {
                    doc_comprovante_endereco: Home,
                    doc_declaracao_escolar: GraduationCap,
                    doc_identificacao_documento_indetificacao: IdCard,
                    doc_titulo_eleitor: IdCard,
                    doc_certificado_reservista: IdCard,
                  }[name] || FileText;

                return (
                  <div
                    key={name}
                    style={{
                      position: "relative",
                      background: "#fff",
                      border: `1px solid ${statusConfig.border}`,
                      borderRadius: 9,
                      padding: "10px 10px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      minHeight: 130,
                      boxSizing: "border-box",
                      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <input
                          type="checkbox"
                          id={name}
                          name={name}
                          checked={formData[name] || false}
                          onChange={handleChange}
                          disabled={isCampoBloqueadoParaEmpresa(name)}
                          style={{
                            cursor: isCampoBloqueadoParaEmpresa(name)
                              ? "not-allowed"
                              : "pointer",
                            width: 14,
                            height: 14,
                          }}
                        />
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: statusConfig.bg,
                          }}
                        >
                          <IconComponent size={14} color="#5f5e5a" />
                        </div>
                      </div>
                      <div>{statusConfig.icon}</div>
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 12.5,
                          color: "#1c1917",
                          lineHeight: 1.25,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {label}
                      </span>
                      <p
                        style={{
                          fontSize: 10.5,
                          margin: "2px 0 0",
                          color: statusConfig.color,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: 500,
                        }}
                        title={statusConfig.label}
                      >
                        {showProgress ? "Enviando…" : statusConfig.label}
                      </p>
                    </div>

                    <label
                      htmlFor={`file-input-${name}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        padding: "6px 12px",
                        borderRadius: 8,
                        background: isAttached ? "#01336d" : "transparent",
                        border: "1px solid #01336d",
                        color: isAttached ? "#ffffff" : "#01336d",
                        fontSize: 11.5,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        cursor: isCampoBloqueadoParaEmpresa(name)
                          ? "not-allowed"
                          : "pointer",
                        opacity: isCampoBloqueadoParaEmpresa(name) ? 0.5 : 1,
                        pointerEvents: isCampoBloqueadoParaEmpresa(name)
                          ? "none"
                          : "auto",
                        transition: "background-color 0.2s ease",
                      }}
                    >
                      {isAttached ? (
                        <Paperclip size={12} />
                      ) : (
                        <Upload size={12} />
                      )}
                      {isAttached ? "Substituir" : "Anexar"}
                    </label>

                    <input
                      id={`file-input-${name}`}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      style={{ display: "none" }}
                      onChange={handleAnexoChange(name)}
                      disabled={isCampoBloqueadoParaEmpresa(name)}
                    />
                  </div>
                );
              })}
            </div>

            <div className="tca-eight-5 tca-mt-3">
              <label>
                Observação: Gentileza anexar os documentos atualizados.
                Documentos ilegíveis ou vencidos podem atrasar a contratação.
              </label>
            </div>
          </div>
        </section>

        <section className="tca-card tca-mb-4" id="tca-section-obs">
          <div className="tca-card-header">
            13. Observações para ajuste na emissão do contrato (CIEE/MG)
          </div>
          <div className="tca-card-body tca-text-center">
            <div className="tca-toggle-group tca-mb-4">
              <button
                type="button"
                className={`tca-toggle-btn ${formData.inserir_observacoes ? "active" : ""}`}
                onClick={() =>
                  setFormData((p) => ({
                    ...p,
                    inserir_observacoes: true,
                    observacoes_ajuste_contrato: "",
                  }))
                }
              >
                {formData.inserir_observacoes
                  ? "✓ Inserir Particularidades para emissão do contrato"
                  : "Inserir Particularidades para emissão do contrato"}
              </button>
              <button
                type="button"
                className={`tca-toggle-btn ${!formData.inserir_observacoes ? "active" : ""}`}
                onClick={() =>
                  setFormData((p) => ({
                    ...p,
                    inserir_observacoes: false,
                    observacoes_ajuste_contrato: "",
                  }))
                }
              >
                {!formData.inserir_observacoes
                  ? "✓ Não Inserir Particularidades para emissão do contrato"
                  : "Não Inserir Particularidades para emissão do contrato"}
              </button>
            </div>
            {formData.inserir_observacoes && (
              <div className="tca-textarea-container tca-visible">
                <textarea
                  className="tca-form-control"
                  rows="4"
                  name="observacoes_ajuste_contrato"
                  value={formData.observacoes_ajuste_contrato}
                  onChange={handleChange}
                  placeholder="Digite aqui."
                ></textarea>
              </div>
            )}
          </div>
        </section>

        {podeAprovar && tcaStatus === "PENDENTE" && (
          <div className="tca-card tca-mb-4">
            <div className="tca-card-header">Aprovação</div>
            <div className="tca-card-body">
              <p>Este TCA está aguardando aprovação.</p>
              <div className="tca-d-flex tca-gap-2">
                <button
                  type="button"
                  className="tca-btn tca-btn-success"
                  onClick={aprovarTcaEmpregador}
                >
                  ✅ Aprovar
                </button>
                <button
                  type="button"
                  className="tca-btn tca-btn-danger"
                  onClick={recusarTcaEmpregador}
                >
                  ❌ Recusar
                </button>
              </div>
            </div>
          </div>
        )}

        {modoEdicao && tcaHistorico.length > 0 && (
          <div className="tca-card tca-mb-4">
            <div className="tca-card-header">Histórico de Transições</div>
            <div className="tca-card-body">
              <ul className="tca-historico-list">
                {tcaHistorico.map((h, i) => {
                  const corAnterior = getStatusCor(
                    h.status_anterior || "início",
                  );
                  const corNovo = getStatusCor(h.status_novo);

                  return (
                    <li key={i} className="tca-historico-item">
                      <div className="tca-historico-status">
                        <span
                          className="tca-historico-badge"
                          style={{ background: corAnterior, color: "#fff" }}
                        >
                          {h.status_anterior || "início"}
                        </span>
                        <span className="tca-historico-seta">→</span>
                        <span
                          className="tca-historico-badge"
                          style={{ background: corNovo, color: "#fff" }}
                        >
                          {h.status_novo}
                        </span>
                      </div>

                      <small className="tca-historico-info">
                        Por: {h.usuario_nome || h.usuario_id} ({h.usuario_tipo})
                      </small>
                      <small className="tca-historico-info">
                        {new Date(h.created_at).toLocaleString("pt-BR")}
                      </small>

                      {h.observacao && (
                        <div className="tca-historico-observacao">
                          {h.observacao}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        <div
          className="tca-d-flex tca-mb-5"
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            marginTop: "2rem",
          }}
        >
          <button
            type="button"
            className="tca-btn tca-btn-secondary"
            onClick={() => navigate("/inicial")}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={openReview}
            className="tca-btn tca-btn-primary"
            disabled={submitting}
          >
            {submitting ? "Enviando…" : "Revisar e Enviar TCA"}
          </button>
        </div>

        {missingFieldsAlert.length > 0 && (
          <MissingFieldsAlert
            missingFields={missingFieldsAlert}
            onFieldClick={handleMissingFieldClick}
            onClose={() => setMissingFieldsAlert([])}
          />
        )}

        {showReview && (
          <div
            className="tca-modal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowReview(false);
            }}
            role="dialog"
            aria-modal="true"
          >
            <div className="tca-modal-content">
              <FormReview
                data={buildReviewData()}
                onEdit={handleEditSection}
                onSubmit={handleConfirmSubmit}
                confirmMessage="Tem certeza que deseja enviar o TCA CIEEMG Empregador?"
              />
            </div>
          </div>
        )}
      </form>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {confirmDialogEmpregador}
    </div>
  );
};

export default TcaCieemgEmpregador;
